import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {useNavigate}                                     from 'react-router-dom'
import {useUserStore}                                    from "../../state"
import {Box, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, Divider, Drawer, IconButton, Popover, Tooltip, Typography, useMediaQuery, useTheme} from "@mui/material"
import {alpha}                                           from "@mui/material/styles"
import {
	Cancel,
	CheckCircle,
	ArrowForward,
	CheckCircleOutline,
	ChevronRight,
	Close,
	DescriptionOutlined,
	DonutLargeOutlined,
	FactCheckOutlined,
	ErrorOutline,
	KeyboardArrowDown,
	KeyboardArrowUp,
	MenuBookOutlined,
	RadioButtonUnchecked,
	Replay,
	WorkspacePremium, Book, BookOutlined
} from "@mui/icons-material"
import {PrepKitBuyContext, PrepKitContext, PrepKitRefreshContext}                                                                       from "./PrepKit"
import {fetchUserProfile, findFirstQuestionInTopic, findQuestionInPrepKit, findTopicInPrepKit} from "../../util/mini"
import {
	countTopicsByFilter,
	NEEDS_WORK_BELOW,
	scoreTier,
	fetchTopicSessions,
	hasMatchingTopic,
	topicMatchesFilter,
	TOPIC_FILTERS,
	isTopicSessionComplete,
	leafTopicsOf,
	restartTopicSession,
	scoreSession,
	sectionActionLabel,
	sectionActionTarget,
	sectionCounts,
	sectionSessionStatus,
	startTopicSession,
	topicActionLabel,
	topicSessionStatus,
	TOPIC_SESSION_STATUS
}                                                        from "../../util/topic-sessions"
import {getLastQuestion}                                 from "../../util/prepkit-utils"
import {splitProductsByType}                             from "../../util/product-types"
import TrialBanner                                       from "./TrialBanner"
import useExpiryCountdown                                from "../../hooks/useExpiryCountdown"

import {FIVE_MINUTES}                                    from "../../util/trial-banner"
import ReadinessMeter                                    from "./ReadinessMeter"
import {PASSING, readinessFromTopics}                    from "../../util/readiness"
import Welcome                                           from "./Welcome"
import Tour                                              from "./Tour"
import {Helmet}                                          from "react-helmet-async"
import LeftRight from "../LeftRight"
import AchievementBadgeIcon                              from "./AchievementBadgeIcon"
import AchievementsPanel                                 from "./AchievementsPanel"
import AchievementToast                                  from "./AchievementToast"
import useAchievementAlerts                              from "../../hooks/useAchievementAlerts"
import {fetchAchievements}                               from "../../util/achievements"

/*
 * The inline Welcome flow is off: ProfileSetupDialog (mounted in PrepKit.js) owns onboarding now,
 * and it fires on a superset of this screen's condition (196). Module scope deliberately -- inside
 * the component it would be a reactive value that the useMemo below has to declare.
 *
 * Flip to true to restore the inline flow for brand-new users.
 */
const INLINE_WELCOME_ENABLED = false


/*
 * Layout debugging (151). Set to 1 to outline the three groups that make up a section header and a
 * topic row, and 0 to hide them:
 *
 *   magenta  the row
 *   red      1. icon + name
 *   lime     2. status + progress
 *   blue     3. actions
 *
 * A WIDTH rather than a boolean on purpose: at 0 the border is still declared, so turning it on
 * cannot change the box model. A border that appears only when debugging would add 2px to every
 * group and shift the very layout it is there to show.
 */
const DEBUG_LAYOUT_BORDER_WIDTH = 0

/*
 * The two fixed columns inside a row's status/progress group, from md up (153).
 *
 * Named because BOTH the section header and the topic rows must use the same pair — that is the
 * whole reason they are fixed rather than content-sized, and four literals across two components
 * is how they drift apart.
 *
 * SCORE is set by the COVERAGE BAR, not the badge (155). The badge's own minWidth is 52, but the
 * in-progress bar spans this whole column, and at 72 it was reading as a dash rather than as a
 * measure. 110 gives it something to fill. (It was 168 once, sized when this column held "12 of 20
 * answered" in words — that was genuinely wasted, since the name is the thing that truncates.)
 *
 * STATUS has to fit "NOT STARTED" and "IN PROGRESS" — 11 characters at fontSize.micro, bold, upper
 * case, 0.07em tracking, and nowrap. That measures ~80px, so this leaves a little over 10px of
 * slack rather than risking an overflow that would push the column out of line.
 *
 * GUTTER is the space after the group, before the actions. It lives inside the group rather than on
 * the outer grid's columnGap, because that gap also separates the NAME from this group and widening
 * it would take from the name on both sides instead of just this one boundary.
 */
const ROW_SCORE_WIDTH = 110
const ROW_STATUS_WIDTH = 92
const ROW_GROUP_GUTTER = 3

/*
 * How far a topic card is indented from the section header above it (157), and how little sits
 * between the card's own edge and its icon (158).
 *
 * These two work as a pair. INDENT moves the card's left edge right; PAD is the space inside the
 * card before the icon. Only PAD changes the gap you see between the card edge and the icon —
 * raising INDENT alone moves the icon along with the edge and leaves the gap identical.
 *
 * So PAD is small and INDENT absorbs the difference: the card now starts close to its icon, and
 * the whitespace reads as the topics being nested under their section rather than as air inside
 * every row.
 *
 * LEFT EDGE ONLY. The right edge is deliberately untouched — the status/progress and action groups
 * are content-sized and pushed right, so anything taken off that side lands on exactly the two
 * columns that have to line up with the section's. Narrowing the card from the left only takes from
 * the name column, which is a 1fr track and absorbs it.
 */
const TOPIC_ROW_INDENT = 5
const TOPIC_ROW_PAD = 0.75

/* ---------- progress helpers (Layer 1 — real data) ---------- */

// leaf topics carry questions; domains carry child topics. Aggregate recursively.
const topicProgress = (topic) => {
	if (topic.topics && topic.topics.length) {
		return topic.topics.reduce((acc, child) => {
			const p = topicProgress(child)
			return {answered: acc.answered + p.answered, total: acc.total + p.total}
		}, {answered: 0, total: 0})
	}
	const qs = topic.questions || []
	return {total: qs.length, answered: qs.filter(q => q.userAnswer).length}
}

/* ---------- small presentational pieces ---------- */

const ProgressReadout = ({answered, total, custom, compact, barOnly}) => {
	const pct = total > 0 ? Math.round((answered / total) * 100) : 0
	return (
		<Box display="flex" alignItems="center" gap={1} minWidth={compact ? 120 : 160}>
			<Box flex={1} height={6} borderRadius={3} sx={{background: custom.progressEmpty, overflow: 'hidden'}}>
				<Box height="100%" width={`${pct}%`} sx={{background: custom.status.green, borderRadius: 3}}/>
			</Box>
			{!barOnly && (
				<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted, whiteSpace: 'nowrap'}}>
					{answered} of {total}
				</Typography>
			)}
		</Box>
	)
}

/*
 * The counts a topic card reads, all scoped to its live session because userAnswer is (101).
 */
const topicCounts = (topic) => {
	const answered = topic.answeredCount || 0
	const total = topic.questionCount || 0
	const correct = topic.correctCount || 0
	const complete = isTopicSessionComplete(topic)
	const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0

	return {answered, total, correct, complete, accuracy}
}

/*
 * A score's colour: green at 75+, amber at 65-74, red below (121).
 *
 * THE only place the dashboard turns a score into a colour. This ladder had been copied into the
 * topic variant, the section header, the daily challenge and the past-attempts list, and three of
 * the four were wrong — two used NEEDS_WORK_BELOW (75) as the amber threshold, which puts the amber
 * branch behind an unreachable condition, so amber never rendered anywhere and a 40% looked exactly
 * like a 70%.
 */
const scoreColor = (accuracy, custom) => {
	switch (scoreTier(accuracy)) {
		case 'PASS': return custom.status.green
		case 'NEAR': return custom.status.amber
		default:     return custom.status.danger
	}
}

/*
 * Per-state presentation for a topic card (107).
 *
 * FIVE states, not three. Completion is not one outcome — finishing at 100%, at 85% and at 45% are
 * different situations calling for different next actions, and flattening them into one green
 * "Completed" hid the only thing a student needs off this screen: which finished topics still owe
 * them work.
 *
 *   Not started    no session
 *   In progress    session open
 *   Needs work     finished under 75%   -> Review (Restart is the icon beside it)
 *   Completed      finished 75-99%      -> Review answers
 *   Mastered       finished at 100%     -> Review answers
 *
 * 75% is the same bar TopicScore congratulates on, the daily-challenge ladder tops out at, and the
 * Needs Work filter chip uses — so a card badged NEEDS WORK is exactly a card that chip finds.
 *
 * `accent` is used at low opacity for the pill and the icon tile and at full strength for the metric
 * and the bottom rule. It is deliberately never a fill behind a large area: the previous design put
 * solid colour on a 52px tile and a 30px score, which is what made a grid of these read as loud.
 */
const topicCardVariant = (topic, custom) => {
	const status = topicSessionStatus(topic)
	const {answered, total, accuracy} = topicCounts(topic)

	if (status === TOPIC_SESSION_STATUS.COMPLETED) {
		if (accuracy >= 100) {
			return {
				status, accent: custom.status.green, Icon: WorkspacePremium,
				pill: 'Mastered',
				metric: {value: <b>100%</b>, label: 'mastery'},
				primary: {label: 'Review', kind: 'review'},
				restartable: true,
			}
		}
		if (accuracy < NEEDS_WORK_BELOW) {
			return {
				// graded: a 70% is amber, a 40% is red. Both still badge as Needs Work.
				status, accent: scoreColor(accuracy, custom), Icon: ErrorOutline,
				pill: 'Needs Work',
				metric: {value: <b>{accuracy}%</b>, label: 'score'},
				/*
				 * Review, like every other finished state. A weak score is arguably better served by
				 * doing the topic again, but the vocabulary here is fixed at Start / Resume / Review
				 * / Restart — a fifth verb makes the reader work out whether it means something new.
				 * Restart is one click away as the icon beside it.
				 */
				primary: {label: 'Review', kind: 'review'},
				restartable: true,
			}
		}
		return {
			status, accent: custom.status.green, Icon: CheckCircleOutline,
			pill: 'Completed',
			metric: {value: <b>{accuracy}%</b>, label: 'score'},
			primary: {label: 'Review', kind: 'review'},
			restartable: true,
		}
	}

	if (status === TOPIC_SESSION_STATUS.IN_PROGRESS) {
		return {
			status, accent: custom.indigo.primary, Icon: MenuBookOutlined,
			pill: 'In Progress',
			metric: {value: <XofY x={answered} y={total} custom={custom} suffix={'answered'}/>},
			primary: {label: 'Resume', kind: 'start'},
			restartable: true,
		}
	}

	/*
	 * `answered`, not a hardcoded 0.
	 *
	 * Normally they are the same number — no session means no in-session answers, so userAnswer is
	 * null on every question and answeredCount is 0. But writing the 0 as a literal made this card
	 * incapable of disagreeing with itself: if a topic ever carries answers without a live session,
	 * the card asserted "0 answered" while the section header above it counted those same answers
	 * and said otherwise. A card that reads its own data can be wrong; one that prints a constant
	 * can only be misleading.
	 */
	return {
		status, accent: custom.text.subtopic, Icon: BookOutlined,
		pill: 'Not Started',
		/*
		 * NO metric (145). "0 of 20 questions" was reporting a count of work done on a topic where
		 * none has been, and a zero beside every untouched topic is noise a reader has to filter
		 * out of a whole section. The NOT STARTED label already says everything the number did.
		 *
		 * Consumers must treat `metric` as optional because of this.
		 */
		metric: null,
		primary: {label: 'Start', kind: 'start'},
		// nothing to restart: there is no attempt to throw away
		restartable: false,
	}
}

/*
 * The action cell: a grid, not a flex row.
 *
 *   [ 32px ] [ 32px ] [ primary button ]
 *
 * Three fixed tracks, always present. An absent icon leaves its track EMPTY rather than collapsing
 * it — the icons vary from none to two, and in a right-anchored flex row that variation moved the
 * primary button to a different x on every row.
 *
 * The button is a fixed width too, because Start / Resume / Review have different natural widths
 * and would otherwise share a right edge and have a ragged left one.
 *
 * THE BUTTON IS LAST (154). The far-right column is the most scannable line in a list — you can run
 * an eye down it without reading anything else — and it used to be occupied by the details icon,
 * the least important control on the row. The primary action belongs there.
 *
 * Within the icons, details comes before restart, so the pointer travelling right toward the button
 * passes over the harmless one. Restart archives an attempt on a single click with no confirmation,
 * and it is now the control nearest the one people are aiming at.
 *
 * Section rows leave both icon tracks empty and so line up with the topics beneath them.
 */
const ACTION_BUTTON_WIDTH = 96
const ACTION_ICON_SLOT = 32

const ActionCell = ({children, sx}) => (
	<Box sx={{
		...sx,
		display: 'grid',
		gridTemplateColumns: `${ACTION_ICON_SLOT}px ${ACTION_ICON_SLOT}px ${ACTION_BUTTON_WIDTH}px`,
		alignItems: 'center',
		justifyItems: 'center',
		gap: 0.5,
		justifyContent: 'end',
	}}>
		{children}
	</Box>
)

// the shared look of a row's one text action. Fixed width: see ActionCell.
const actionButtonSx = (custom) => ({
	width: '100%',
	minWidth: 0,
	textTransform: 'none',
	borderRadius: `${custom.radius.sm}px`,
	px: 1,
	py: 0.5,
	whiteSpace: 'nowrap',
	fontSize: custom.fontSize.sm,
	fontWeight: custom.fontWeight.semibold,
	color: custom.text.main,
	background: custom.bg.raised,
	borderColor: custom.border.color,
	'&:hover': {borderColor: custom.border.strong, background: custom.bg.dropdownHover},
})

// an icon slot that holds its space when there is nothing to put in it
const ActionIconSlot = ({title, dataGa, onClick, children}) => (
	children
		? (
			<Tooltip title={title} arrow>
				<IconButton size="small" data-ga={dataGa} onClick={onClick} sx={{color: 'inherit'}}>
					{children}
				</IconButton>
			</Tooltip>
		)
		: <Box/>
)

/*
 * The score, as a filled badge (137).
 *
 * White on a solid status colour rather than coloured text. As text these hues had to clear 4.5:1
 * against the page, which is what forced them dark enough to read muddy (120); as a fill they carry
 * white at 5.0-5.9:1 and can be as saturated as they like. It also gives the number a definite
 * edge, so a column of them scans as a column.
 *
 * Every state gets one, so the column is never ragged: a score once finished, a count while in
 * progress, and the topic's size before it is started.
 */
const ScoreBadge = ({custom, value, color, sx}) => (
	<Box sx={{
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 52,
		px: 0.875,
		py: 0.375,
		borderRadius: `${custom.radius.sm}px`,
		background: color,
		color: '#fff',
		fontSize: custom.fontSize.xs,
		fontWeight: custom.fontWeight.bold,
		lineHeight: 1.4,
		whiteSpace: 'nowrap',
		...sx,
	}}>
		{value}
	</Box>
)

/*
 * The coverage bar, drawn under the count it visualises (114).
 *
 * ABSOLUTELY POSITIONED, so it contributes no height to its cell. In flow it made the cell taller
 * than the plain text cells beside it, and the grid's alignItems:center then centred that taller
 * box — which pushed the number itself above the row's midline while every other value sat on it.
 * Out of flow, the cell is exactly as tall as its text and the number centres with the rest.
 *
 * This also removes the need to reserve space in the other states: nothing below can be displaced
 * by something that takes up no room, so row heights stay uniform whether the bar is there or not.
 *
 * Shown ONLY while a topic or section is in progress. Not Started is always 0% and anything
 * finished is always 100% — a bar that is always empty or always full is ink, not information, and
 * both facts are already stated by the label and the count beside it. In progress is the one state
 * where the fill length says something the number does not say faster.
 *
 * The space is reserved in every state regardless, so rows keep a uniform height and the columns
 * either side stay on their baselines instead of jumping row to row.
 *
 * Blue always: it measures HOW FAR, never how well.
 */
const CoverageBar = ({custom, percent, show}) => (
	!show ? null : (
		<Box sx={{
			position: 'absolute',
			left: 0,
			right: 0,
			// just clear of the text's descenders, in the row's own padding
			top: 'calc(100% + 5px)',
			height: 3,
		}}>
			<Box sx={{
				height: '100%',
				borderRadius: 999,
				background: custom.progressEmpty,
				overflow: 'hidden',
			}}>
			<Box sx={{
					height: '100%',
					width: `${percent}%`,
					background: custom.indigo.primary,
					borderRadius: 999,
					transition: 'width 300ms',
				}}/>
			</Box>
		</Box>
	)
)

/*
 * One topic, as a row — the only view of a topic since 159. Five states, and the arrangement
 * differs, so the two views cannot drift in what they say about a topic.
 */
// see DEBUG_LAYOUT_BORDER_WIDTH at the top of the file
const dbg = (colour) => ({border: `${DEBUG_LAYOUT_BORDER_WIDTH}px dashed ${colour}`})

/*
 * One topic, as a row. Three groups (149):
 *
 *   1. icon + name        2. status + progress        3. actions
 *
 * md and up is one line, unchanged: the three sit side by side with group 2 on fixed sub-widths so
 * the status and badge stay on the same axes as the section header above them.
 *
 * Below md it becomes a 2x2 block:
 *
 *     [icon] Overview of Florida Real Estate Statutes
 *            (80%) COMPLETED                    [ REVIEW ][↺][⋯]
 *
 * A real grid rather than flexWrap, because that is what this is — the first row spans both
 * columns so a long name gets the FULL width, which matters at exactly the size where names were
 * truncating. Status and score stay paired on the left, and the actions take the bottom-right,
 * which is the easiest corner to reach on a phone.
 *
 * Deliberately NOT name-left/score-right on the top line: a not-started topic has no badge (145),
 * so that arrangement leaves an empty top-right corner on every untouched row. Here every state
 * fills the same shape.
 */
const TopicRow = ({topic, custom, busy, onAction, onDetails}) => {
	const v = topicCardVariant(topic, custom)
	const {answered, total, accuracy} = topicCounts(topic)
	const pct = total > 0 ? Math.round((answered / total) * 100) : 0
	const status = topicSessionStatus(topic)
	const inProgress = status === TOPIC_SESSION_STATUS.IN_PROGRESS
	const tint = (a) => alpha(v.accent, a)

	// in-progress is the one state whose status label takes a colour; see 113 for why the rest do not
	const statusColor = inProgress ? custom.indigo.primary : undefined

	// a score once finished, a count while working, nothing before starting (145)
	const badge = status === TOPIC_SESSION_STATUS.COMPLETED
		? {value: `${accuracy}%`, color: scoreColor(accuracy, custom)}
		: inProgress
			? {value: `${answered}/${total}`, color: custom.indigo.primary}
			: null

	return (
		<Box
			data-ga="topic-card"
			/*
			 * The WHOLE ROW takes the primary action (183). The label on the right says what that
			 * action is; it is no longer the only place you can click to get it.
			 *
			 * The row is not a <button>: it contains two of them (details, restart), and a button
			 * inside a button is invalid. The label stays a real button so keyboard and screen-reader
			 * users still have a proper target — the row is a convenience on top of it, and every
			 * control inside stops propagation so a secondary action never fires the primary one too.
			 */
			onClick={busy ? undefined : () => onAction(topic, v.primary.kind)}
			sx={{
				display: 'grid',
				/*
				 * xs: two columns, name spanning both on the first row.
				 * md: one row — name takes the slack, group 2 and the actions are content-sized.
				 */
				gridTemplateColumns: {xs: 'minmax(0, 1fr) auto', md: 'minmax(0, 1fr) auto auto'},
				alignItems: 'center',
				// tight between the name and the line under it; md is one row so it never applies
				rowGap:  {xs: 0, md: 0.25},
				columnGap: 2,
				py: 1.25,
				// asymmetric: the left space is now the container's indent, the right must still
				// match the section header's so the action cell stays on its axis
				pl: {xs: 0, md: TOPIC_ROW_PAD},
				pr: 2,
				/*
				 * Each row is its own faint card (156), rather than a bare strip on a tinted block.
				 *
				 * raised / raisedHover is the token pair built for this — "cards that sit ON a
				 * surface: lighter than it in dark, subtly darker in light". So the default reads as
				 * a barely-there card against the product panel, and hover pulls FURTHER from the
				 * panel in both themes: darker in light, lighter in dark. Hard-coding a darkening
				 * would have washed the row into the panel in dark mode.
				 *
				 * No divider either — the gap between cards separates them now.
				 */
				background: custom.bg.raised,
				borderRadius: `${custom.radius.md}px`,
				cursor: busy ? 'default' : 'pointer',
				transition: 'background 150ms',
				'&:hover': {background: custom.bg.raisedHover},
				// the chevron leans into the direction of travel on row hover, not just label hover
				'&:hover .topic-row-chevron': {transform: 'translate(2px, -50%)'},
				...dbg('magenta'),
			}}
		>
			{/* ---- 1. icon + name ---- */}
			<Box sx={{
				gridColumn: {xs: '1 / span 2', md: 'auto'},
				gridRow: {xs: '1', md: 'auto'},
				display: 'flex',
				alignItems: 'center',
				gap: 1.5,
				minWidth: 0,
				...dbg('red'),
			}}>
				<Box sx={{
					flexShrink: 0,
					width: {xs: 16, md: 32},
					height: {xs: 26, md: 32},
					ml: {xs: 1.5, md: 0.5},
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					borderRadius: `${custom.radius.sm}px`,
					background: {xs: 'transparent', md: tint(0.1)},
					color: v.accent,
				}}>
					<v.Icon sx={{fontSize: {xs: 19, md: 17}}}/>
				</Box>

				<Tooltip title={topic.name} arrow enterDelay={800}>
					<Box component="span" sx={{
						minWidth: 0,
						fontSize: {xs: custom.fontSize.lg, md: custom.fontSize.md},
						fontWeight: {xs: custom.fontWeight.bold, md: custom.fontWeight.semibold},
						color: custom.text.main,
						lineHeight: 1.35,
						// one line: a row's height is what keeps a long list scannable
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					}}>
						{topic.name}
					</Box>
				</Tooltip>
			</Box>

			{/* ---- 2. status + progress ---- */}
			<Box sx={{
				gridColumn: {xs: '1', md: 'auto'},
				gridRow: {xs: '2', md: 'auto'},
				display: 'flex',
				alignItems: 'center',
				gap: {xs: 2, md: 6},
				minWidth: 0,
				// lines up under the name rather than under the icon, once wrapped
				pl: {xs: 5, md: 0},
				// see ROW_GROUP_GUTTER
				pr: {xs: 0, md: ROW_GROUP_GUTTER},
				...dbg('lime'),
			}}>
				{/* badge first on xs, status first on md — see the `order` values */}
				<Box sx={{
					width: {md: ROW_SCORE_WIDTH},
					position: 'relative',
					flexShrink: 0,
					order: {xs: 1, md: 2},
				}}>
					{badge && <ScoreBadge custom={custom} value={badge.value} color={badge.color}/>}
					<CoverageBar custom={custom} percent={pct} show={inProgress}/>
				</Box>

				<Box component="span" sx={{
					width: {md: ROW_STATUS_WIDTH},
					textAlign: {xs: 'left', md: 'center'},
					fontSize: custom.fontSize.micro,
					fontWeight: custom.fontWeight.bold,
					textTransform: 'uppercase',
					letterSpacing: '0.07em',
					color: statusColor || custom.text.muted,
					whiteSpace: 'nowrap',
					order: {xs: 2, md: 1},
					mt: 0.5,
				}}>
					{v.pill}
				</Box>
			</Box>

			{/* ---- 3. actions ---- */}
			<ActionCell sx={{
				gridColumn: {xs: '2', md: 'auto'},
				gridRow: {xs: '2', md: 'auto'},
				justifySelf: 'end',
				...dbg('blue'),
			}}>
				{/*
				  * FactCheckOutlined, not MoreHoriz (154). MoreHoriz is the universal overflow-menu
				  * glyph — it promises a menu of choices and this opens a single panel, so the icon
				  * set up an expectation every click broke. A ticked list says what is actually in
				  * there: every question, marked right or wrong.
				  *
				  * Deliberately not a chevron: this screen already uses ChevronRight for collapse in
				  * SupplementalRow AND for navigation on the HUD cards, so a third meaning would be
				  * ambiguous against both.
				  */}
				<ActionIconSlot
					title="Topic details"
					dataGa="topic-details"
					onClick={(e) => { e.stopPropagation(); onDetails(topic) }}
				>
					<FactCheckOutlined sx={{fontSize: 18, color: custom.text.subtopic}}/>
				</ActionIconSlot>

				{busy
					? <CircularProgress size={14} sx={{color: custom.text.muted}}/>
					: (
						<ActionIconSlot
							title="Restart this topic"
							dataGa="topic-study"
							onClick={(e) => { e.stopPropagation(); onAction(topic, 'restart') }}
						>
							{v.restartable
								? <Replay sx={{fontSize: 18, color: custom.text.subtopic, transform: 'scaleX(-1)'}}/>
								: null}
						</ActionIconSlot>
					)}

				{/*
				  * A label and a chevron, not a bordered button (183). The row is the button now, so
				  * a second box drawn around the label claimed the click for itself and made the
				  * other 90% of the row look inert.
				  *
				  * IT KEEPS THE 96px TRACK AND CENTRES THE LABEL IN IT, which is what holds it on the
				  * same axis as the section header's button above — that button centres its own label
				  * in the identical track. The chevron is taken OUT OF FLOW and pinned right, so it
				  * cannot shift the label off that centre; in a flex row the pair would centre as a
				  * group and every topic label would sit half a chevron left of its section's.
				  */}
				<Box
					component="button"
					type="button"
					data-ga="topic-study"
					disabled={busy}
					onClick={(e) => { e.stopPropagation(); onAction(topic, v.primary.kind) }}
					sx={{
						font: 'inherit',
						position: 'relative',
						width: '100%',
						px: 1,
						py: 0.5,
						border: 'none',
						background: 'transparent',
						cursor: busy ? 'default' : 'pointer',
						textAlign: 'center',
						whiteSpace: 'nowrap',
						fontSize: custom.fontSize.sm,
						fontWeight: custom.fontWeight.semibold,
						color: custom.text.main,
						opacity: busy ? 0.5 : 1,
					}}
				>
					{v.primary.label}
					<ChevronRight className="topic-row-chevron" sx={{
						position: 'absolute',
						right: 0,
						top: '50%',
						transform: 'translateY(-50%)',
						fontSize: 18,
						color: custom.text.subtopic,
						transition: 'transform 150ms',
					}}/>
				</Box>
			</ActionCell>
		</Box>
	)
}

/*
 * The topic filter chips (104), lifted out of the kit header when that header was removed (108).
 *
 * One selection applies across every product and every section at once, so the question being asked
 * is "what have I not finished?" of the whole kit rather than of one product at a time. They are
 * rendered once, in the first visible product's header, but they are not scoped to it.
 *
 * A chip that would match nothing is disabled rather than hidden: chips appearing and disappearing
 * as you study would make the row jump, and "Needs Work: none" is itself useful to see. All is
 * never disabled, so there is always a way back to the full list.
 */
/*
 * The five topic filters, as chips at every width (173).
 *
 * They used to collapse into a dropdown below md. They no longer do: five short labels shrink and
 * wrap into two centred lines on a phone, and a visible row shows the CURRENT filter and the other
 * four options at once — a dropdown showed neither until opened, which is a poor trade for a control
 * whose whole job is to say what you are looking at.
 */
const TopicFilters = ({custom, filter, counts, onFilter}) => (
	<Box sx={{
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexWrap: 'wrap',
		gap: {xs: 0.75, md: 1},
	}}>
		{TOPIC_FILTERS.map(f => {
			const selected = filter === f.key
			const empty = f.key !== 'ALL' && counts && counts[f.key] === 0

			return (
				<Box
					key={f.key}
					component="button"
					type="button"
					data-ga="topic-filter"
					data-ga-value={f.key}
					disabled={empty}
					onClick={() => onFilter(f.key)}
					sx={{
						font: 'inherit',
						// tighter on a phone so all five fit two lines at 375px rather than three
						px: {xs: 1.25, md: 1.75},
						py: {xs: 0.5, md: 0.75},
						borderRadius: 999,
						cursor: empty ? 'default' : 'pointer',
						whiteSpace: 'nowrap',
						fontSize: {xs: custom.fontSize.xs, md: custom.fontSize.sm},
						// 400. The token scale stops at medium (600) because nothing in this app
						// needed body weight in a control until now; a literal is honest here rather
						// than adding a scale entry with one caller.
						fontWeight: 400,
						transition: 'background 150ms, border-color 150ms, color 150ms',
						// no fill (189): the chips sit on the page rather than on a surface of their own
						background: 'transparent',
						/*
						 * Selected is carried by the BORDER, not a fill (106).
						 *
						 * The selected outline was text.main — near-black in light, near-white in dark,
						 * the hardest edge either theme has. Softened to text.subtopic (189): still a
						 * clear step above both the resting border.color and the border.strong an
						 * unselected chip takes on hover, so nothing else in the row can be mistaken
						 * for it, without a black ring around a word.
						 *
						 * The label does the rest of the work. Unselected labels drop to text.muted, so
						 * selection is now two quiet differences — a firmer edge AND full-strength text
						 * — rather than one loud one.
						 */
						...(selected
							? {
								color: custom.text.main,
								border: `1.5px solid ${custom.text.subtopic}`,
							}
							: {
								color: empty ? custom.text.subtopic : custom.text.muted,
								border: `1.5px solid ${custom.border.color}`,
								opacity: empty ? 0.5 : 1,
								// hover still fills: a resting transparent chip needs something to
								// answer the pointer with
								...(empty ? {} : {'&:hover': {borderColor: custom.border.strong, background: custom.bg.dropdownHover, color: custom.text.main}}),
							}),
					}}
				>
					{f.label}
				</Box>
			)
		})}
	</Box>
)

const XofY = ({custom, prefix, x, y, suffix}) => (
	<Box sx={{fontWeight: custom.fontWeight.normal, color: custom.text.muted}}>
		{prefix ? `${prefix} ` : ''}
		{x !== undefined && <Box component="span" sx={{fontWeight: custom.fontWeight.bold, color: custom.text.main}}>{x}</Box>}
		{x !== undefined && ' of '}
		{y !== undefined && <Box component="span" sx={{fontWeight: custom.fontWeight.bold, color: custom.text.main}}>{y}</Box>}
		{suffix ? ` ${suffix}` : ''}
	</Box>
)

// "3 Sep 2026" — enough to tell two attempts apart without being a timestamp
const sessionDate = (value) => value
	? new Date(value).toLocaleDateString(undefined, {day: 'numeric', month: 'short', year: 'numeric'})
	: null

/*
 * Past attempts at this topic (101). Each archived session, its dates, and what it scored.
 *
 * This is the "retained for past-performance display" half of the session history. It is
 * deliberately a compact list rather than a second question breakdown — the question detail above
 * is about the attempt in progress, and a drawer that repeated it per attempt would bury it.
 */
const PastAttempts = ({sessions, questionCount, custom, loading}) => {
	if (loading) {
		return (
			<Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 1}}>
				<CircularProgress size={14} sx={{color: custom.text.muted}}/>
				<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted}}>
					Loading past attempts…
				</Typography>
			</Box>
		)
	}

	if (!sessions || sessions.length === 0) return null

	return (
		<Box sx={{mt: 1}}>
			<Typography sx={{
				fontSize: custom.fontSize.xxs, fontWeight: custom.fontWeight.semibold,
				textTransform: 'uppercase', letterSpacing: .5, color: custom.text.subtopic, mb: 1,
			}}>
				Past attempts
			</Typography>

			{sessions.map((session) => {
				const score = scoreSession(session, questionCount)
				const finished = Boolean(session.completed_at)
				const color = finished ? scoreColor(score.accuracy, custom) : custom.text.muted

				return (
					<Box key={session.id} sx={{
						display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
						py: 0.75,
						borderTop: `1px solid ${custom.border.color}`,
					}}>
						<Box sx={{minWidth: 0}}>
							<Typography sx={{fontSize: custom.fontSize.sm, color: custom.text.main}}>
								{sessionDate(session.started_at)}
							</Typography>
							<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted}}>
								{finished
									? `Completed ${sessionDate(session.completed_at)}`
									: `Not finished — ${score.answered} of ${score.total} answered`}
							</Typography>
						</Box>
						<Typography sx={{
							flexShrink: 0, fontSize: custom.fontSize.sm,
							fontWeight: custom.fontWeight.semibold, color,
						}}>
							{score.answered ? `${score.accuracy}%` : '—'}
						</Typography>
					</Box>
				)
			})}
		</Box>
	)
}

/*
 * The topic details drawer. Same panel as before, now explicitly scoped to the LIVE session (101):
 * the question list shows this attempt's answers, the header states when the attempt began, and
 * past attempts are listed underneath.
 */
const TopicDetailsDrawer = ({topic, prepKitId, custom, onClose, onAction, onQuestion}) => {
	const open = Boolean(topic)
	const {answered, total, correct, complete, accuracy} = topicCounts(topic || {})
	const questions = topic?.questions || []
	const session = topic?.session
	const status = topicSessionStatus(topic || {})

	const [history, setHistory] = useState(null)
	const [loadingHistory, setLoadingHistory] = useState(false)

	// history is only worth a request while the drawer is open, and only for the topic in it
	useEffect(() => {
		if (!topic || !prepKitId) {
			setHistory(null)
			return
		}

		let cancelled = false
		setLoadingHistory(true)
		setHistory(null)

		fetchTopicSessions(prepKitId, topic.id)
			.then(data => { if (!cancelled) setHistory(data.history || []) })
			.catch(() => { if (!cancelled) setHistory([]) })
			.finally(() => { if (!cancelled) setLoadingHistory(false) })

		return () => { cancelled = true }
	}, [topic, prepKitId])

	const actions = complete
		? [{label: 'Review', kind: 'review'}, {label: 'Restart', kind: 'restart'}]
		: [{label: topicActionLabel(topic || {}), kind: 'start'}]

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{sx: {width: {xs: '100%', sm: 440}, background: custom.bg.surface, color: custom.text.main}}}
		>
			{topic && (
				<Box sx={{p: 3}}>
					<Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1}}>
						<Typography sx={{fontSize: custom.fontSize.lg, fontWeight: custom.fontWeight.bold, color: custom.text.main}}>
							{topic.name}
						</Typography>
						<IconButton size="small" onClick={onClose}><Close fontSize="small"/></IconButton>
					</Box>

					<Typography sx={{mt: 0.5, fontSize: custom.fontSize.sm, color: custom.text.muted}}>
						{status === TOPIC_SESSION_STATUS.NOT_STARTED
							? `Not started — ${total} questions`
							: complete
								? `Score: ${accuracy}%  (${correct} / ${answered} correct)`
								: `Progress: ${answered} / ${total} answered`}
					</Typography>

					{/* which attempt the list below belongs to */}
					{session && (
						<Typography sx={{mt: 0.25, fontSize: custom.fontSize.xs, color: custom.text.subtopic}}>
							{complete
								? `This attempt: ${sessionDate(session.started_at)} → ${sessionDate(session.completed_at)}`
								: `This attempt started ${sessionDate(session.started_at)}`}
						</Typography>
					)}

					<Box sx={{display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap'}}>
						{actions.map((action, i) => (
							<Button
								key={action.kind}
								variant={i === 0 ? 'contained' : 'outlined'}
								disableElevation
								onClick={() => onAction(topic, action.kind)}
								sx={{
									textTransform: 'none',
									borderRadius: `${custom.radius.sm}px`,
									px: 2,
									...(i === 0
										? {background: custom.indigo.primary, color: '#fff', '&:hover': {background: custom.indigo.hover}}
										: {color: custom.text.main, borderColor: custom.border.strong, '&:hover': {borderColor: custom.text.muted, background: custom.bg.dropdownHover}}),
								}}
							>
								{action.label} →
							</Button>
						))}
					</Box>

					<Divider sx={{my: 2, borderColor: custom.border.color}}/>

					<Box>
						{questions.map((q, i) => {
							const answeredQ = Boolean(q.userAnswer)
							const isCorrect = answeredQ && q.userAnswer.is_correct
							const icon = !answeredQ
								? <RadioButtonUnchecked sx={{fontSize: 18, color: custom.text.subtopic}}/>
								: isCorrect
									? <CheckCircle sx={{fontSize: 18, color: custom.status.green}}/>
									: <Cancel sx={{fontSize: 18, color: custom.status.danger}}/>
							const clickable = Boolean(q.code)
							return (
								<Box
									key={q.id || i}
									data-ga="question-item"
									onClick={clickable ? () => onQuestion(q) : undefined}
									sx={{
										display: 'flex', gap: 1.25, py: 0.75, px: 1, mx: -1, alignItems: 'flex-start',
										borderRadius: `${custom.radius.sm}px`,
										cursor: clickable ? 'pointer' : 'default',
										'&:hover': clickable ? {background: custom.bg.dropdownHover} : undefined,
									}}
								>
									<Box sx={{mt: '2px', flexShrink: 0}}>{icon}</Box>
									<Typography sx={{fontSize: custom.fontSize.sm, color: answeredQ ? custom.text.main : custom.text.muted}}>
										{q.question}
									</Typography>
								</Box>
							)
						})}
					</Box>

					{(loadingHistory || (history && history.length > 0)) && (
						<Divider sx={{my: 2, borderColor: custom.border.color}}/>
					)}
					<PastAttempts
						sessions={history}
						questionCount={total}
						custom={custom}
						loading={loadingHistory}
					/>
				</Box>
			)}
		</Drawer>
	)
}

/*
 * A section (a product's direct child topic). Its button is START -> RESUME -> REVIEW only (101):
 * there is no Restart at section level, because a restart discards an attempt and that is a
 * decision to make one topic at a time.
 */
const DomainAccordion = ({topic, index, custom, filter, busyTopicId, onSectionAction, onTopicAction, onDetails}) => {
	const [open, setOpen] = useState(index === 0)

	// the section's own counts stay whole — they describe the section, not the current view
	const children = (topic.topics || []).filter(c => topicMatchesFilter(c, filter))

	/*
	 * A filtered view is always expanded. Collapsing is for skipping past material you are not
	 * interested in; having asked for exactly one kind of topic, hiding the matches behind a
	 * chevron would defeat the question just asked.
	 */
	const filtering = Boolean(filter) && filter !== 'ALL'
	const expanded = open || filtering

	/*
	 * The section's state, and the colour that carries it (111).
	 *
	 * The colour is tied to the SCORE — red / amber / green on the same 75 / 65 thresholds used
	 * everywhere else — not to whether the section is finished. A section three-quarters done and
	 * getting them all wrong should read red while it is happening, not turn red only at the end.
	 *
	 * Grey until something is answered, because there is no score to grade yet and 0% would
	 * otherwise paint every untouched section red.
	 *
	 * This is the counterpart to the coverage bar being permanently indigo: the bar says how FAR,
	 * these two say how WELL, and neither has to do the other's job.
	 */
	const counts = sectionCounts(topic)
	const sectionStatus = sectionSessionStatus(topic)
	const sec = {
		complete: sectionStatus === TOPIC_SESSION_STATUS.COMPLETED,
		inProgress: sectionStatus === TOPIC_SESSION_STATUS.IN_PROGRESS,
		label: sectionStatus === TOPIC_SESSION_STATUS.COMPLETED
			? 'Completed'
			: sectionStatus === TOPIC_SESSION_STATUS.IN_PROGRESS ? 'In Progress' : 'Not Started',
		// grey until something is answered: there is no score to grade yet, and 0% would otherwise
		// paint every untouched section red
		accent: counts.answered === 0 ? custom.text.subtopic : scoreColor(counts.accuracy, custom),
	}
	return (
		<Box>
			<Box
				data-ga="subtopic-toggle"
				onClick={() => setOpen(o => !o)}
				sx={{
					/*
					 * The same three groups and the same 2x2 wrap as the topic rows below (150), so
					 * a section and its topics stay on one set of axes at every width.
					 */
					display: 'grid',
					gridTemplateColumns: {xs: 'minmax(0, 1fr) auto', md: 'minmax(0, 1fr) auto auto'},
					alignItems: 'center',
					rowGap: 0.25,
					columnGap: 2,
					py: 1.5,
					px: {xs: 0, md: 0},
					cursor: 'pointer',
					borderBottom: `1px solid ${custom.border.color}`,
					...dbg('magenta'),
				}}
			>
				{/* ---- 1. chevron + name ---- */}
				<Box sx={{
					gridColumn: {xs: '1 / span 2', md: 'auto'},
					gridRow: {xs: '1', md: 'auto'},
					display: 'flex',
					alignItems: 'center',
					gap: 2.5,
					minWidth: 0,
					...dbg('red'),
				}}>
					{/*
					  * The one chevron left on this screen. The product above no longer collapses, so
					  * it is unambiguous: it opens and closes a section and nothing else. It points UP
					  * when open — the arrow shows where the content goes if you press it, not where
					  * it currently is.
					  */}
					<Box
						component="button"
						type="button"
						aria-expanded={expanded}
						aria-label={`${expanded ? 'Collapse' : 'Expand'} ${topic.name}`}
						// the row toggles too, so let the row's handler do it once rather than twice
						onClick={(e) => e.preventDefault()}
						sx={{
							flexShrink: 0,
							width: {xs: 26, md: 22},
							height: {xs: 26, md: 22},
							p: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							borderRadius: `${custom.radius.sm}px`,
							border: 'none',
							cursor: 'pointer',
							background: custom.chip.bg,
							color: custom.text.main,
							transition: 'background 150ms',
							'&:hover': {background: custom.bg.raisedHover},
						}}
					>
						{expanded
							? <KeyboardArrowUp sx={{fontSize: {xs: 19, md: 17}}}/>
							: <KeyboardArrowDown sx={{fontSize: {xs: 19, md: 17}}}/>}
					</Box>

					<Typography sx={{
						minWidth: 0,
						fontSize: {xs: custom.fontSize.lg, md: 16},
						fontWeight: custom.fontWeight.bold,
						color: custom.text.main,
						lineHeight: 1.35,
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					}}>
						{topic.name}
					</Typography>
				</Box>

				{/* ---- 2. status + progress ---- */}
				<Box sx={{
					gridColumn: {xs: '1', md: 'auto'},
					gridRow: {xs: '2', md: 'auto'},
					display: 'flex',
					alignItems: 'center',
					gap: {xs: 2, md: 6},
					minWidth: 0,
					// lines up under the name rather than under the chevron, once wrapped
					pl: {xs: 6, md: 0},
					// see ROW_GROUP_GUTTER
					pr: {xs: 0, md: ROW_GROUP_GUTTER},
					...dbg('lime'),
				}}>
					<Box sx={{
						width: {md: ROW_SCORE_WIDTH},
						position: 'relative',
						flexShrink: 0,
						order: {xs: 1, md: 2},
					}}>
						{/* empty until something is answered (145), like the topics' */}
						{counts.answered > 0 && (
							<ScoreBadge
								custom={custom}
								value={sec.complete ? `${counts.accuracy}%` : `${counts.answered}/${counts.total}`}
								color={sec.complete ? scoreColor(counts.accuracy, custom) : custom.indigo.primary}
							/>
						)}
						<CoverageBar custom={custom} percent={counts.percent} show={Boolean(sec.inProgress)}/>
					</Box>

					<Box component="span" sx={{
						width: {md: ROW_STATUS_WIDTH},
						textAlign: {xs: 'left', md: 'center'},
						fontSize: custom.fontSize.micro,
						fontWeight: custom.fontWeight.bold,
						textTransform: 'uppercase',
						letterSpacing: '0.07em',
						color: sec.inProgress ? custom.indigo.primary : custom.text.muted,
						whiteSpace: 'nowrap',
						order: {xs: 2, md: 1},
						mt: 0.5
					}}>
						{sec.label}
					</Box>
				</Box>

				{/* ---- 3. actions: the trio, both icon tracks empty so it lands on the topics' axis ---- */}
				<ActionCell sx={{
					gridColumn: {xs: '2', md: 'auto'},
					gridRow: {xs: '2', md: 'auto'},
					justifySelf: 'end',
					pr: 2,
					...dbg('blue'),
				}}>
					{/* both icon tracks empty, so the button lands on the topics' below */}
					<Box/>
					<Box/>
					<Button
						variant="outlined"
						data-ga="section-study"
						onClick={(e) => { e.stopPropagation(); onSectionAction(topic) }}
						sx={actionButtonSx(custom)}
					>
						{sectionActionLabel(topic)}
					</Button>
				</ActionCell>
			</Box>

			{/*
			  * MUI Collapse rather than maxHeight:0 + overflow:hidden.
			  *
			  * That pairing animated only `opacity`, so the height snapped while the fade ran — and
			  * more importantly a zero-height box still has its content in the accessibility tree and
			  * still tab-focusable, so a collapsed section kept catching keyboard focus on buttons
			  * nobody could see. `unmountOnExit` takes the cards out of the tree entirely.
			  */}
			<Collapse in={expanded} timeout={200} unmountOnExit>
			<Box sx={{pl: 0, pt: 0, borderBottom: `0px solid ${custom.border.color}`}}>
				{children.length
					? (
						/*
						 * Just a spacer (156). The tint that used to live here moved onto the rows
						 * themselves, so this only sets the rhythm between them.
						 *
						 * No horizontal padding: the whole point of the shared column widths is that
						 * a section's badge sits directly above its topics', and an inset here lands
						 * on exactly the columns that must line up.
						 */
								<Box sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 1,
									mb: 1.5,
									// left only — see TOPIC_ROW_INDENT
									pl: {xs: 5, md: TOPIC_ROW_INDENT},

								}}>
									{children.map(c => (
										<TopicRow
											key={c.id}
											topic={c}
											custom={custom}
											busy={busyTopicId === c.id}
											onAction={onTopicAction}
											onDetails={onDetails}
										/>
									))}
								</Box>
					)
					: <Typography component="span" sx={{fontSize: custom.fontSize.sm, color: custom.text.muted}}>No topics yet.</Typography>}
			</Box>
			</Collapse>
		</Box>
	)
}

/*
 * A primary product. ALWAYS EXPANDED — there is no collapse (101).
 *
 * The dashboard was three levels of expandable nesting (product > section > topics), which meant
 * the student could hide their own course behind two clicks and had to remember which of two
 * chevrons did what. A product is the thing they bought; there is no reading of this screen where
 * hiding it entirely is the useful state. Sections keep their chevron, so exactly one level
 * collapses and the affordance means one thing.
 *
 * With the chevron gone the title moves into the space it occupied and steps up a size, so the
 * product reads as the page's heading rather than as a row with a control on it.
 *
 * `defaultOpen` is gone with the collapse; the caller no longer passes it.
 *
 * The header reads left to right as identity then progress: medallion, name, the kit's size in
 * questions and sections, and on the right a labelled overall bar with the answered count. The
 * size line replaces nothing — it was the one fact about a kit the dashboard never stated.
 */
const ProductCard = ({product, custom, filter, busyTopicId, onSectionAction, onTopicAction, onDetails}) => {
	/*
	 * A product whose every section is filtered out drops off the page rather than sitting empty.
	 * `sections` below stays the WHOLE count — it is a fact about the product, stated in the size
	 * line, and should not shrink because of what the reader is currently filtering for.
	 */
	const visibleSections = (product.topics || []).filter(sec => hasMatchingTopic(sec, filter))
	if (visibleSections.length === 0) return null

	const {total} = topicProgress({topics: product.topics || []})
	const sections = (product.topics || []).length

	return (
		/*
		 * No fill (118). bg.surface and bg.main are the SAME colour in light mode (#FFFFFF), so
		 * this painted an invisible panel there and a clearly visible box in dark, where they are
		 * #1A1C2E on #0F101A. The product is not a card — it is a heading with its sections under
		 * it — so it sits flat on the page and now looks the same in both themes.
		 */
		<Box sx={{mb: 2, overflow: 'hidden'}}>
			{/*
			  * The header row. Not a button: nothing here toggles, so it takes no pointer and no
			  * hover. A single hairline closes it off from the sections below — the old 3px dotted
			  * rule sat ON TOP of the card, which read as a divider between products rather than as
			  * the base of this one.
			  */}
			<Box sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1.5,
				py: 2,
				px: 0,
				borderBottom: `1px solid ${custom.border.color}`,
			}}>
				<Box sx={{flex: 1, minWidth: 0}}>
					<Typography sx={{
						// a step above fontSize.display (20), the top of the token scale — just enough to
						// sit clearly above the sections. The product is the page's heading, not a banner.
						fontSize: 22,
						fontWeight: custom.fontWeight.bold,
						color: custom.text.main,
						lineHeight: 1.2
					}}>
						{product.name}
					</Typography>
					{/* the size of the thing they bought, in one muted line under its name */}
					<Typography sx={{
						fontSize: custom.fontSize.sm,
						color: custom.text.muted,
						lineHeight: 1.4,
						mt: 0.25,
					}}>
						{total} question{total === 1 ? '' : 's'} · {sections} section{sections === 1 ? '' : 's'}
					</Typography>
				</Box>

				{/*
				  * The product's own readiness, summed from its leaf topics (172).
				  *
				  * NO DOT here — that is the prepkit meter's anchor and says "you have started", which
				  * is true of the account rather than of each product. Repeated down the page it would
				  * read as a per-product state that can differ, and it cannot.
				  *
				  * Sits where the filter chips used to (108, 171): the header's one right-hand slot,
				  * opposite the product name. It reads the WHOLE product, not the filtered view —
				  * readiness describes the product, not what is currently on screen.
				  */}
				<Box sx={{display: {xs: 'none', sm: 'flex'}, flexShrink: 0, mr: 2}}>
					<ReadinessMeter
						readiness={readinessFromTopics(leafTopicsOf(product))}
						custom={custom}
						showDot={false}
						dashWidth={16}
					/>
				</Box>
			</Box>
			<Box>
				{visibleSections.map((domain, i) => (
					<DomainAccordion key={domain.id} topic={domain} index={i} custom={custom} filter={filter} busyTopicId={busyTopicId} onSectionAction={onSectionAction} onTopicAction={onTopicAction} onDetails={onDetails}/>
				))}
			</Box>
		</Box>
	)
}

/*
 * Supplemental + media products (068). Deliberately NOT a stand-out card: flat background,
 * hairline border, no shadow, smaller type. Still fully expandable so the material remains
 * usable -- it is subordinate, not hidden.
 *
 * Renders nothing when the prepkit has no supplemental products, which is the common case.
 */
const SupplementalSection = ({products, custom, filter, busyTopicId, onSectionAction, onTopicAction, onDetails}) => {
	// same rule as ProductCard: nothing matching, nothing shown — including the heading
	const shown = (products || []).filter(p => hasMatchingTopic(p, filter))
	if (shown.length === 0) return null

	return (
		<Box sx={{mt: 3, mb: 2}}>
			<Typography sx={{
				fontSize: custom.fontSize.sm,
				fontWeight: custom.fontWeight.semibold,
				textTransform: 'uppercase',
				letterSpacing: '0.08em',
				color: custom.text.muted,
				mb: 1,
			}}>
				Supplemental Study Materials
			</Typography>
			<Box sx={{
				border: `1px solid ${custom.border.color}`,
				borderRadius: `${custom.radius.md}px`,
				overflow: 'hidden',
			}}>
				{shown.map((product, i) => (
					<SupplementalRow
						key={product.id}
						product={product}
						custom={custom}
						divider={i > 0}
						filter={filter}
						busyTopicId={busyTopicId}
						onSectionAction={onSectionAction}
						onTopicAction={onTopicAction}
						onDetails={onDetails}
					/>
				))}
			</Box>
		</Box>
	)
}

const SupplementalRow = ({product, custom, divider, filter, busyTopicId, onSectionAction, onTopicAction, onDetails}) => {
	// hooks first: an early return above them would change the hook order between renders
	const [open, setOpen] = useState(false)
	const {answered, total} = topicProgress({topics: product.topics || []})

	const sections = (product.topics || []).filter(sec => hasMatchingTopic(sec, filter))
	if (sections.length === 0) return null

	return (
		<Box sx={divider ? {borderTop: `1px solid ${custom.border.color}`} : undefined}>
			<Box
				data-ga="section-toggle"
				onClick={() => setOpen(o => !o)}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					px: {xs: 0, md: 1.5},
					py: {xs: 0, md: 1.5},
					cursor: 'pointer'
				}}
			>
				<IconButton size="small" sx={{transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s'}}>
					<ChevronRight fontSize="small"/>
				</IconButton>
				<Typography sx={{flex: 1, fontSize: custom.fontSize.base, fontWeight: custom.fontWeight.semibold, color: custom.text.main}}>
					{product.name}
				</Typography>
				<ProgressReadout answered={answered} total={total} custom={custom}/>
			</Box>
			{open && (
				<Box sx={{p: 1.5, pb: 1.5}}>
					{sections.map((domain, i) => (
						<DomainAccordion key={domain.id} topic={domain} index={i} custom={custom} filter={filter} busyTopicId={busyTopicId} onSectionAction={onSectionAction} onTopicAction={onTopicAction} onDetails={onDetails}/>
					))}
				</Box>
			)}
		</Box>
	)
}

/* ---------- Layer 2 — the HUD strip ---------- */

// accent-tinted disc holding a card's emoji, so the three medallions read as one family
const HudMedallion = ({custom, accent, children, size = 56}) => (
	<Box sx={{
		width: size, height: size, flexShrink: 0, borderRadius: '50%',
		background: alpha(accent, 0.14), border: `1px solid ${alpha(accent, 0.35)}`,
		display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
	}}>
		{children}
	</Box>
)

/*
 * One HUD card (072). All three share this anatomy — medallion, uppercase label, large value,
 * caption — so the row reads as a set of dashboard tiles rather than three unrelated boxes.
 *
 * No accent stripe (141). 073 gave each card a 3px coloured top border to tell the three apart;
 * the tinted medallion already does that, and the stripe was the same signal a second time — three
 * different colours across one row, on the element the cards have in common. Dropping it takes the
 * `accent` prop with it, since the border was all it fed. The medallions keep their own colours.
 */
const HudCard = ({custom, medallion, label, value, caption, trailing, onClick}) => (
	<Box
		onClick={onClick}
		sx={{
			flex: '1 1 260px',
			minWidth: 240,
			display: 'flex',
			alignItems: 'center',
			gap: 1.75,
			background: custom.bg.surface,
			border: `1px solid ${custom.border.color}`,
			borderRadius: `${custom.radius.lg}px`,
			boxShadow: custom.shadow.card,
			p: 2.25,
			...(onClick ? {
				cursor: 'pointer',
				transition: 'transform 150ms, box-shadow 150ms, background 150ms',
				'&:hover': {
					background: custom.bg.surfaceHover,
					boxShadow: custom.shadow.pop,
					transform: 'translateY(-2px)',
				},
			} : {}),
		}}
	>
		{medallion}
		<Box sx={{minWidth: 0, flex: 1}}>
			<Typography noWrap sx={{
				fontSize: custom.fontSize.micro, fontWeight: custom.fontWeight.semibold,
				textTransform: 'uppercase', letterSpacing: .6, color: custom.text.muted,
			}}>
				{label}
			</Typography>
			<Typography noWrap sx={{
				fontSize: custom.fontSize.display, fontWeight: custom.fontWeight.bold,
				color: custom.text.main, lineHeight: 1.2,
			}}>
				{value}
			</Typography>
			<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted}}>
				{caption}
			</Typography>
		</Box>
		{trailing}
	</Box>
)

/*
 * Achievements card. Real data: earned count plus the most recently earned badges as a preview.
 */
const AchievementsTile = ({custom, achievements, onOpen}) => {
	const earned = achievements ? achievements.earnedCount : null
	const total = achievements ? achievements.earnableCount : null

	// most recent first, so the preview shows what they just unlocked
	const recent = achievements
		? achievements.achievements
			.filter(a => a.earned)
			.sort((a, b) => new Date(b.earnedAt) - new Date(a.earnedAt))
			.slice(0, 3)
		: []

	return (
		<HudCard
			custom={custom}
			medallion={<HudMedallion custom={custom} accent={custom.hud.streak}>🏅</HudMedallion>}
			label="Achievements"
			value={earned === null ? '—' : `${earned} of ${total}`}
			caption={earned === null
				? 'Loading your badges…'
				: earned === 0
					? 'Earn your first badge.'
					: 'Badges earned so far.'}
			trailing={recent.length > 0
				? (
					// slight overlap so three badges read as a collection, not a row of buttons
					<Box sx={{display: 'flex', flexShrink: 0, pl: 0.5}}>
						{recent.map((a, i) => (
							<Box key={a.token} sx={{ml: i === 0 ? 0 : '-8px'}}>
								<AchievementBadgeIcon token={a.token} category={a.category} size={30} title={a.name}/>
							</Box>
						))}
					</Box>
				)
				: <ChevronRight sx={{color: custom.text.muted, flexShrink: 0}}/>
			}
			onClick={onOpen}
		/>
	)
}

/*
 * A HUD tile that has nothing true to say yet (167).
 *
 * Exam Readiness and Study Insights are both derived from answer history. Before the first topic is
 * finished there is barely any, so the readiness ring was drawing a percentage off a handful of
 * questions and the insights report opened effectively empty. Both now explain what they are and
 * what will switch them on.
 *
 * A different anatomy to HudCard on purpose: no medallion, no value, no caption. The description IS
 * the content, so it gets the room the value would have taken, and the tile cannot be mistaken for
 * one that is reporting a number.
 *
 * DELIBERATELY NOT CLICKABLE, and no chevron. The live Study Insights tile opens the report; while
 * warming up there is nothing there worth opening, so this neither accepts a click nor advertises
 * one — the same call 141 made for Exam Readiness.
 */
const WarmingUpCard = ({custom, icon, label, description}) => (
	<Box sx={{
		flex: '1 1 260px',
		minWidth: 240,
		display: 'flex',
		flexDirection: 'column',
		gap: 1,
		background: custom.bg.surface,
		// dashed, the one place on the dashboard that is: it reads as a tile waiting to be filled
		// rather than a tile that is finished and merely quiet
		border: `1px dashed ${custom.border.strong}`,
		borderRadius: `${custom.radius.lg}px`,
		p: 2.25,
	}}>
		<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
			{icon}
			<Typography sx={{
				fontSize: custom.fontSize.md,
				fontWeight: custom.fontWeight.bold,
				color: custom.text.main,
				lineHeight: 1.2,
			}}>
				{label}
			</Typography>

			{/*
			  * The pill is pushed right and never wraps. It is the status, so it has to stay on the
			  * label's line — dropped below, it reads as a heading for the description underneath.
			  */}
			<Box component="span" sx={{
				ml: 'auto',
				flexShrink: 0,
				px: 1.25,
				py: 0.375,
				borderRadius: 999,
				background: custom.chip.bg,
				border: `1px solid ${custom.chip.border}`,
				color: custom.chip.text,
				fontSize: custom.fontSize.micro,
				fontWeight: custom.fontWeight.bold,
				textTransform: 'uppercase',
				letterSpacing: '0.08em',
				whiteSpace: 'nowrap',
			}}>
				Warming up
			</Box>
		</Box>

		<Typography sx={{fontSize: custom.fontSize.xs, color: custom.text.muted, lineHeight: 1.55}}>
			{description}
		</Typography>
	</Box>
)

/*
 * Exam Readiness: the five-dash meter, with a popover explaining it (171).
 *
 * CLICKABLE AGAIN. 141 took the click off this card because it opened the same Study Insights report
 * as the Performance card two tiles along — one row with two different-looking doors to one room.
 * The objection was the duplication, not the clicking: this opens content that exists nowhere else.
 *
 * The popover is FORWARD-LOOKING rather than an account of what has happened (175). It used to read
 * "You have banked 1 of 68 topics", which is accurate and useless: it tells a student where they are
 * without telling them what moves the needle. The tagline names the state, the line under it names
 * what earns the next bar and what four bars means.
 *
 * Naming individual topics is the job of the topic list directly below it.
 */
const ReadinessTile = ({custom, readiness}) => {
	const [anchorEl, setAnchorEl] = useState(null)

	if (!readiness) return null

	// finished, but not well enough to bank: the one number that explains a stalled meter
	const shortfall = Math.max(0, readiness.completed - readiness.passed)

	return (
		<>
			<HudCard
				custom={custom}
				medallion={
					<HudMedallion custom={custom} accent={custom.status.greenStrong}>
						{/*
						  * A white halo on the glyph (185). The cap is a dark-drawn emoji and the disc
						  * behind it is a 14% tint over bg.surface, which is nearly black in dark mode;
						  * the drop-shadow outlines the artwork instead of lightening the disc, so the
						  * medallions keep the look they have.
						  *
						  * Unconditional: in light mode the halo is white on an all-but-white disc and
						  * has nothing to show.
						  */}
						<Box component="span" sx={{filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))'}}>
							🎓
						</Box>
					</HudMedallion>
				}
				label="Exam Readiness"
				value={`${readiness.percent}%`}
				caption={`${readiness.passed} of ${readiness.total} topics passed.`}
				trailing={<ReadinessMeter readiness={readiness} custom={custom} showLabel={false} dashWidth={16}/>}
				onClick={(e) => setAnchorEl(e.currentTarget)}
			/>

			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
				PaperProps={{sx: {
					mt: 1,
					maxWidth: 340,
					p: 2.25,
					background: custom.bg.surface,
					border: `1px solid ${custom.border.color}`,
					borderRadius: `${custom.radius.lg}px`,
					backgroundImage: 'none',
					boxShadow: custom.shadow.pop,
				}}}
			>
				<ReadinessMeter readiness={readiness} custom={custom} showLabel={false}/>

				{/*
				  * Supplied copy (187). One heading and one explanation, the same at every rung —
				  * replacing the tagline ladder (readinessTagline / readinessMessage), which wrote a
				  * different pair of lines for each of nine states. The thing a student opens this to
				  * learn is what the bars MEAN, and that does not change as they fill.
				  */}
				<Typography sx={{
					mt: 1.5,
					fontSize: custom.fontSize.lg,
					fontWeight: custom.fontWeight.bold,
					color: custom.text.main,
				}}>
					More green bars = closer to exam-ready
				</Typography>

				<Typography sx={{mt: 0.75, fontSize: custom.fontSize.sm, color: custom.text.muted, lineHeight: 1.55}}>
					Your readiness reflects % Complete and Score. Topics at {PASSING}% or higher add to
					your green bars.
				</Typography>

				{/*
				  * The one piece of counting worth keeping: a finished topic under the bar is the
				  * cheapest bar available, because the work is done and only the score is short.
				  */}
				{shortfall > 0 && (
					<Typography sx={{mt: 1, fontSize: custom.fontSize.sm, color: custom.text.muted, lineHeight: 1.55}}>
						{shortfall === 1
							? `Currently, 1 completed topic is below ${PASSING}%, so it isn't adding yet. Revisiting it can help move your readiness forward.`
							: `Currently, ${shortfall} completed topics are below ${PASSING}%, so they aren't adding yet. Revisiting them can help move your readiness forward.`}
					</Typography>
				)}

				{readiness.star && (
					<Typography sx={{mt: 1, fontSize: custom.fontSize.sm, color: custom.text.main, lineHeight: 1.55}}>
						⭐ Every topic complete, averaging {readiness.avgScore}% — top marks.
					</Typography>
				)}
			</Popover>
		</>
	)
}

/*
 * HUD strip. Three cards, told apart by their medallions (073, stripes removed in 141):
 *
 *   Exam Readiness  a green ring showing the score itself
 *   Achievements    hud.streak — gold, already this app's badge colour (AchievementToast)
 *   Study Insights  indigo.primary — the brand accent, and the report is where the row leads
 *
 * Achievements and Study Insights were once both #6366F1 (hud.readiness and indigo.primary are the
 * same hex), which is why they read as the same card — so those two colours stay deliberately
 * chosen rather than arbitrary.
 *
 * hud.perf (cyan) is NOT used here: it sits 31° from status.green on the wheel, too close to tell
 * apart in a tinted medallion.
 *
 * `warmingUp` swaps Readiness and Insights for WarmingUpCards until the first topic is complete
 * (167). Achievements stays live in both modes — welcome-aboard is earned on first load, so that
 * tile always has something real in it, which is what stops the strip reading as three dead boxes.
 */
const HudStrip = ({custom, readiness, onInsights, achievements, onAchievements, warmingUp}) => (
	<Box display="flex" gap={2} flexWrap="wrap" sx={{mb: 2.5}}>
		{/*
		  * Readiness (171): the proportion of primary leaf topics BANKED — finished at 75% or
		  * better — shown as five dashes. Replaces the 071 measure of questions-ever-correct over
		  * total questions, which could read high for a student who had answered a handful well
		  * and finished nothing.
		  */}
		{warmingUp
			? (
				<WarmingUpCard
					custom={custom}
					icon={<DonutLargeOutlined sx={{fontSize: 18, color: custom.text.muted}}/>}
					label="Exam Readiness"
					description="Watch your readiness take shape as you complete more topics."
				/>
			)
			: <ReadinessTile custom={custom} readiness={readiness}/>
		}
		<AchievementsTile custom={custom} achievements={achievements} onOpen={onAchievements}/>
		{warmingUp
			? (
				<WarmingUpCard
					custom={custom}
					icon={<Box component="span" sx={{fontSize: 16, lineHeight: 1}}>🎯</Box>}
					label="Study Insights"
					description="Find out where you’re strong and where to focus, based on how you’ve done so far."
				/>
			)
			: (
				<HudCard
					custom={custom}
					medallion={<HudMedallion custom={custom} accent={custom.indigo.primary}>🎯</HudMedallion>}
					label="Performance"
					value="Study Insights"
					caption="See what's going well and where to focus."
					trailing={<ChevronRight sx={{color: custom.text.muted, flexShrink: 0}}/>}
					onClick={onInsights}
				/>
			)
		}
	</Box>
)

/*
 * Daily Challenge (074). The rungs a section is pushed up, in order.
 */
/*
 * The rungs a topic is challenged up, on the same scale the rest of the dashboard grades with:
 * 65 is where a score stops being red, 75 is passing — the bar TopicScore congratulates on and the
 * Needs Work badge sits below.
 *
 * There is no rung above 75, because a topic at 75 is passing and is no longer a candidate. Adding
 * 100 would turn the banner into a permanent nag on topics the student has already done well at.
 */
const CHALLENGE_TIERS = [65, 75]

/*
 * The first topic that was FINISHED but not passed, with the next rung to aim for (116).
 *
 * Topic-level, not section-level. A section's score is an average across ten or twenty topics, so
 * "push Real Estate Laws from 58% to 65%" named something too big to act on in one sitting and gave
 * no clue where the weakness actually was. A topic is one sitting's work and has one Restart.
 *
 * Only COMPLETED topics qualify, for two reasons:
 *
 *   1. "Your score is too low" is only a coherent thing to say about a topic that is finished. A
 *      topic mid-attempt has a score that is still moving on its own.
 *   2. It makes the metric honest. The old section version deliberately used ever-correct over ALL
 *      questions, because correct-over-answered reads 100% for a student who got their only two
 *      attempts right and left eighteen untouched. On a completed topic every question has been
 *      answered, so the two measures are the same number and that distortion cannot occur.
 *
 * Products and topics are walked in page order, so the challenge points at the first weak topic the
 * student would meet reading down the screen.
 *
 * Returns null once every finished topic is at 75% or better — there is nothing left to challenge.
 * Expects scorePrepKit to have run. Pass PRIMARY products only.
 */
const findDailyChallenge = (products) => {
	/*
	 * Three passes, in order of what is worth asking for (174).
	 *
	 * IMPROVE first — a finished topic below the bar is the cheapest readiness a student can win,
	 * because the work is already done and only the score is short.
	 *
	 * The two fallbacks exist because IMPROVE alone left the card BLANK for anyone whose finished
	 * topics were all passing. Finish one topic at 80% and the slot went empty, with the rest of the
	 * course untouched and nothing on the page naming a next step: "nothing to improve" was being
	 * treated as "nothing to do".
	 */
	const all = (products || []).flatMap(leafTopicsOf).filter(t => topicCounts(t).total > 0)

	// 1. finished, but under the bar
	for (const topic of all) {
		if (!isTopicSessionComplete(topic)) continue

		const {answered, accuracy} = topicCounts(topic)
		if (!answered) continue

		// the same rounded value drives the copy and the tier, so it can never read "75% to 75%"
		const target = CHALLENGE_TIERS.find(t => accuracy < t)
		if (!target) continue

		return {kind: 'IMPROVE', topic, percent: accuracy, target}
	}

	// 2. started and left unfinished — finishing beats starting something new
	for (const topic of all) {
		if (isTopicSessionComplete(topic)) continue
		if (topicSessionStatus(topic) !== TOPIC_SESSION_STATUS.IN_PROGRESS) continue

		const {answered, total} = topicCounts(topic)
		return {kind: 'FINISH', topic, answered, total}
	}

	// 3. the next topic never opened, in page order
	for (const topic of all) {
		if (topicSessionStatus(topic) !== TOPIC_SESSION_STATUS.NOT_STARTED) continue

		const {total} = topicCounts(topic)
		return {kind: 'START_NEXT', topic, total}
	}

	// every topic finished and passing: there is genuinely nothing left to ask for
	return null
}

/*
 * How far into the kit the student is (144). Three states, because the page says something
 * different in each:
 *
 *   NEW        nothing answered, ever      -> the onboarding panel, and "Start Studying"
 *   STARTED    answering, nothing finished -> "finish your first topic"
 *   GOING      at least one topic finished -> the standing challenge, first non-passing topic
 *
 * `answers` is the FULL log, not the session-scoped answeredCount. A student who restarted every
 * topic has a session-scoped count of zero but is plainly not new, and showing them "jump into your
 * first question" would be wrong. The log only ever grows, so NEW means what it says.
 */
const PROGRESS = {NEW: 'NEW', STARTED: 'STARTED', GOING: 'GOING'}

const studentProgress = (prepKit, products) => {
	const everAnswered = Boolean(prepKit && prepKit.answers && prepKit.answers.length)
	if (!everAnswered) return PROGRESS.NEW

	const finishedOne = (products || []).some(p => leafTopicsOf(p).some(isTopicSessionComplete))
	return finishedOne ? PROGRESS.GOING : PROGRESS.STARTED
}

/*
 * The topic to finish first: the one whose session started earliest (144).
 *
 * "First" by when they began it, not by its place in the course — a student who skipped ahead to
 * section 4 should be pointed back at section 4, which is where their attention already is.
 *
 * Only reached in the STARTED state, where by definition no topic is complete, so the earliest
 * session is always still open.
 */
const findFirstTopicChallenge = (products) => {
	let earliest = null

	for (const product of products || []) {
		for (const topic of leafTopicsOf(product)) {
			const session = topic.session
			if (!session || isTopicSessionComplete(topic)) continue

			const {total} = topicCounts(topic)
			if (!total) continue

			if (!earliest || new Date(session.started_at) < new Date(earliest.session.started_at)) {
				earliest = topic
			}
		}
	}

	if (!earliest) return null

	const {answered, total} = topicCounts(earliest)
	return {kind: 'FINISH_FIRST', topic: earliest, answered, total}
}

/*
 * The onboarding panel, shown until the very first question is answered (144).
 *
 * Replaces the Daily Challenge rather than sitting beside it: both occupy the same slot and both
 * answer "what should I do next", and a student with nothing answered has exactly one useful next
 * step. Filled rather than outlined — it is the only thing on the screen asking to be pressed.
 */
const FirstQuestionPanel = ({custom, onStart}) => (
	<Box sx={{
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 3,
		flexWrap: 'wrap',
		p: {xs: 2.5, md: 3},
		mb: 3,
		borderRadius: `${custom.radius.lg}px`,
		background: `linear-gradient(100deg, ${custom.indigo.primary}, ${custom.indigo.border})`,
	}}>
		<Box sx={{minWidth: 0, flex: '1 1 320px'}}>
			<Typography sx={{
				fontSize: custom.fontSize.xs,
				fontWeight: custom.fontWeight.bold,
				textTransform: 'uppercase',
				letterSpacing: '0.09em',
				// on a saturated fill, white at partial alpha reads as a quieter white rather than
				// as a grey, which any neutral token would
				color: 'rgba(255, 255, 255, 0.75)',
			}}>
				Start here
			</Typography>

			<Typography sx={{
				mt: 0.75,
				fontSize: {xs: 20, md: 24},
				fontWeight: custom.fontWeight.bold,
				color: '#fff',
				lineHeight: 1.25,
			}}>
				See What One Question Reveals
			</Typography>

			<Typography sx={{
				mt: 0.75,
				fontSize: custom.fontSize.md,
				color: 'rgba(255, 255, 255, 0.85)',
				lineHeight: 1.5,
				maxWidth: 520,
			}}>
				No setup. No guessing where to begin. Answer one question and instantly see what you know, what you missed, and why.
			</Typography>
		</Box>

		<Button
			data-ga="start-studying"
			onClick={onStart}
			endIcon={<ArrowForward sx={{fontSize: 18}}/>}
			sx={{
				flexShrink: 0,
				textTransform: 'none',
				borderRadius: `${custom.radius.sm}px`,
				px: 3,
				py: 1.25,
				fontSize: custom.fontSize.md,
				fontWeight: custom.fontWeight.bold,
				whiteSpace: 'nowrap',
				background: '#fff',
				color: custom.indigo.primary,
				'&:hover': {background: 'rgba(255, 255, 255, 0.9)'},
			}}
		>
			Start My First Question
		</Button>
	</Box>
)

const DailyChallenge = ({theme, custom, challenge, onAccept}) => {
	if (!challenge) return null

	const {kind, topic} = challenge
	// FINISH_FIRST (the STARTED state) and FINISH (a later half-done topic) read the same way
	const finishing = kind === 'FINISH_FIRST' || kind === 'FINISH'
	const improve = kind === 'IMPROVE'

	// the score's own colour; only the improve challenge has a score to grade
	const currentColor = improve ? scoreColor(challenge.percent, custom) : custom.text.main

	/*
	 * No panel. Sitting on the page rather than in a bordered box puts it in the same layer as the
	 * greeting above it, which is what it is — a line addressed to the student, not a component of
	 * the course list below.
	 * background: custom.bg.surface,
			border: `1px solid ${custom.border.color}`,
	 */
	return (
		<Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3, background: custom.bg.surface,
			border: `1px solid ${custom.border.color}`, borderRadius: 4, px: 3, py: 2.5}}>
			{/*
			  * The label sits ABOVE the sentence (160), not beside it. Inline, the sentence started
			  * at a different x depending on how wide "Daily Challenge" rendered, and on a narrow
			  * screen it wrapped into a ragged block hanging off the label's right edge. Stacked,
			  * the sentence gets the full width and a predictable left edge.
			  */}
			<Box sx={{flex: 1, minWidth: 240}}>
				<Typography sx={{fontSize: custom.fontSize.xs, fontWeight: custom.fontWeight.bold, textTransform: 'uppercase', letterSpacing: .6, color: custom.hud.streak, whiteSpace: 'nowrap'}}>
					Daily Challenge
				</Typography>

				<Typography sx={{mt: 0.25, fontSize: custom.fontSize.lg, color: custom.text.main, lineHeight: 1.4}}>
				{improve
					? (
						<>
							{'Retake '}
							<Box component="span" sx={{fontWeight: custom.fontWeight.semibold}}>"{topic.name}"</Box>
							{' and push it from '}
							<Box component="span" sx={{fontWeight: custom.fontWeight.semibold, color: currentColor}}>{challenge.percent}%</Box>
							{' to '}
							<Box component="span" sx={{fontWeight: custom.fontWeight.semibold, color: custom.status.green}}>{challenge.target}%</Box>
							{'.'}
						</>
					)
					: finishing
						? (
							/*
							 * A topic in progress, not a score to push (144). Nothing is finished, so
							 * there is no accuracy worth grading, and "finish what you started" is the
							 * one next step that always makes sense.
							 */
							<>
								{kind === 'FINISH_FIRST' ? 'Finish your first topic — ' : 'Finish '}
								<Box component="span" sx={{fontWeight: custom.fontWeight.semibold}}>"{topic.name}"</Box>
								{'. You are '}
								<Box component="span" sx={{fontWeight: custom.fontWeight.semibold}}>
									{challenge.answered} of {challenge.total}
								</Box>
								{' questions in.'}
							</>
						)
						: (
							/*
							 * Nothing to improve and nothing half-done, but the course is not finished
							 * (174). Names the next topic rather than leaving the slot empty.
							 */
							<>
								{'Take on '}
								<Box component="span" sx={{fontWeight: custom.fontWeight.semibold}}>"{topic.name}"</Box>
								{' — '}
								<Box component="span" sx={{fontWeight: custom.fontWeight.semibold}}>
									{challenge.total}
								</Box>
								{challenge.total === 1 ? ' question' : ' questions'}
								{' to go.'}
							</>
						)}
				</Typography>
			</Box>

			{/*
			  * A text control, not a filled button (178).
			  *
			  * It has now been both of the obvious things and neither worked. Filled indigo made it a
			  * twin of Continue Studying in the greeting row — same colour, same weight, read as the
			  * same control offered twice. Filled amber fixed that and introduced a worse problem:
			  * light-mode amber is #D97706, and a button-sized field of it is burnt orange.
			  *
			  * So the colour comes off the FIELD and goes onto a glyph. A green tick carries the
			  * "accept" meaning in the one place saturated colour is cheap — 18px of it — and the
			  * label drops back to text.main, which is near-black in light and near-white in dark and
			  * therefore always legible with no contrast arithmetic at all.
			  *
			  * The trailing chevron is the same muted ChevronRight the Study Insights card uses, so
			  * "this leads somewhere" is said the same way twice on one screen rather than two ways.
			  * It replaces the "→" that used to be inside the label.
			  *
			  * px: 0 and minWidth: 0 strip MUI's button padding so the chevron lands on the card's own
			  * right inset, level with the text above it — a button-shaped gap would have pushed it in
			  * and left the edge ragged.
			  */}
			<Button
				onClick={() => onAccept(challenge)}
				disableRipple
				sx={{
					flexShrink: 0,
					textTransform: 'none',
					px: 0,
					py: 0.5,
					minWidth: 0,
					gap: 0.75,
					color: custom.text.main,
					fontSize: custom.fontSize.sm,
					fontWeight: custom.fontWeight.semibold,
					whiteSpace: 'nowrap',
					background: 'transparent',
					// no fill at rest and none on hover; the chevron carries the response instead
					'&:hover': {background: 'transparent'},
					'&:hover .challenge-chevron': {color: custom.text.main, transform: 'translateX(2px)'},
				}}
			>
				<CheckCircle sx={{fontSize: 18, color: custom.status.green}}/>
				{improve ? 'Accept Challenge' : finishing ? 'Keep Going' : 'Start Topic'}
				<ChevronRight className="challenge-chevron" sx={{fontSize: 20, color: custom.text.muted, transition: 'color 150ms, transform 150ms'}}/>
			</Button>
		</Box>
	)
}

/* ---------- main ---------- */

const PrepKitDashboard = React.memo(() => {
	const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('md'))
	const theme = useTheme()
	const custom = theme.custom
	const navigate = useNavigate()
	const {user} = useUserStore()
	const prepKit = useContext(PrepKitContext)
	// refetch + rescore, so a session change is visible without a page reload (101)
	const refreshPrepKit = useContext(PrepKitRefreshContext)
	const openBuy = useContext(PrepKitBuyContext)

	/*
	 * The trial countdown for the in-flow banner (169).
	 *
	 * Only a LIVE TRIAL takes this slot. A paid PrepKit nearing its end keeps the navbar chip and
	 * never displaces the Daily Challenge — the slot is the trial-to-challenge progression, and a
	 * renewal notice is not a study prompt.
	 *
	 * The hook ticks, so `expired` flips on its own and the Daily Challenge appears the second the
	 * trial runs out.
	 */
	const {msLeft: trialMsLeft, expired: trialExpired} = useExpiryCountdown(prepKit && prepKit.expires_at)
	/*
	 * ANY kit that is running out, not only a trial (193). This was gated on is_trial, and
	 * ExpiringPrepKit deliberately renders nothing on the dashboard -- so a paid kit got no warning
	 * here at any point, right up to the moment it expired.
	 *
	 * A trial counts down throughout, because it is 35 minutes long and converting is the point. A
	 * paid kit joins at five minutes: a year-long licence showing a countdown on every dashboard
	 * load would be noise. Below that the shared tiers take over -- five minutes, then the last
	 * minute in seconds and red -- and at zero PrepKit.js opens ExtendOverlay on its own.
	 */
	const isTrialKit = Boolean(prepKit && prepKit.is_trial)
	const showExpiryBanner = Boolean(prepKit) && !trialExpired && trialMsLeft !== null
		&& (isTrialKit || trialMsLeft <= FIVE_MINUTES)

	const [userProfile, setUserProfile] = useState(null)
	const [welcomeComplete, setWelcomeComplete] = useState(false)
	const [detailsTopic, setDetailsTopic] = useState(null)
	// the topic whose restart is waiting on an answer (181); null when nothing is being asked
	const [restartTopic, setRestartTopic] = useState(null)
	// the topic whose session call is in flight — disables its buttons so a double click cannot
	// restart a topic twice
	const [busyTopicId, setBusyTopicId] = useState(null)
	// which topic filter chip is active; applies across every product and section at once (104)
	const [topicFilter, setTopicFilter] = useState('ALL')
	const [sessionError, setSessionError] = useState(null)
	const [achievements, setAchievements] = useState(null)
	const [showAchievements, setShowAchievements] = useState(false)

	/*
	 * Achievements. The hook evaluates on mount (idempotent server-side) and queues anything
	 * newly earned for the toast; earnedCount changing is the signal to reload the collection
	 * so the tile and panel reflect a badge awarded just now.
	 */
	const {current: newBadge, remaining: moreBadges, dismiss: dismissBadge, earnedCount} =
		useAchievementAlerts(prepKit && prepKit.id, {evaluateOnMount: true})

	useEffect(() => {
		if (!prepKit) return
		let cancelled = false
		fetchAchievements(prepKit.id).then(data => { if (!cancelled) setAchievements(data) })
		return () => { cancelled = true }
	}, [prepKit, earnedCount])

	/*
	 * Onboarding moved to ProfileSetupDialog, mounted in PrepKit.js (196).
	 *
	 * That dialog fires on a SUPERSET of this condition -- not welcomed, OR no name, OR no tutor
	 * style -- and covers the study screens as well as this one. Leaving the inline flow live would
	 * stack onboarding behind the modal for exactly the students who get both.
	 *
	 * The condition below is left EXACTLY as it was and switched off by one flag, rather than deleted:
	 * `showWelcome` gates six other things on this screen, Welcome.js is untouched, and restoring the
	 * inline flow for brand-new users is then a one-word change. See code-changes/196.
	 */
	const showWelcome = useMemo(
		() => INLINE_WELCOME_ENABLED && prepKit && userProfile && !userProfile.welcomed_at && !welcomeComplete,
		[prepKit, userProfile, welcomeComplete]
	)

	// primary products get stand-out cards; supplemental + media are grouped below (068)
	const {primary: primaryProducts, supplemental: supplementalProducts} = useMemo(
		() => splitProductsByType(prepKit?.products),
		[prepKit]
	)

	// exam readiness for the HUD tile (071); reads the counts scorePrepKit put on the prepkit
	/*
	 * Readiness over every primary leaf topic (171).
	 *
	 * Primary only, which is why it is built here rather than from the prepkit: splitProductsByType
	 * has already done the primary/supplemental split and a supplemental product is extra material,
	 * not part of the exam being prepared for.
	 */
	const readiness = useMemo(
		() => readinessFromTopics(primaryProducts.flatMap(leafTopicsOf)),
		[primaryProducts]
	)

	// first section below 75%, and the rung to push it to (074). Primary products only.
	/*
	 * Which of the three states the student is in, and the challenge that goes with it (144).
	 *
	 * STARTED points at the topic they began first; GOING falls back to the standing challenge. The
	 * two are never both shown — they occupy one slot and both answer "what next".
	 */
	const progress = useMemo(() => studentProgress(prepKit, primaryProducts), [prepKit, primaryProducts])

	const challenge = useMemo(() => {
		if (progress === PROGRESS.NEW) return null
		if (progress === PROGRESS.STARTED) return findFirstTopicChallenge(primaryProducts)
		return findDailyChallenge(primaryProducts)
	}, [progress, primaryProducts])

	// how many topics each chip would show, so an empty one can be disabled (104)
	const filterCounts = useMemo(
		() => countTopicsByFilter([...primaryProducts, ...supplementalProducts]),
		[primaryProducts, supplementalProducts]
	)

	/*
	 * Which primary products will actually render. ProductCard returns null when the filter empties
	 * it, so the chips have to be attached to the first product that SURVIVES the filter — pinning
	 * them to index 0 would take them off the page the moment that product filtered out, leaving no
	 * way to change the filter back.
	 */
	const visiblePrimary = useMemo(
		() => primaryProducts.filter(p => hasMatchingTopic(p, topicFilter)),
		[primaryProducts, topicFilter]
	)

	useEffect(() => {
		if (user && !userProfile) {
			fetchUserProfile(user.id).then(setUserProfile)
		}
	}, [user, userProfile])

	/*
	 * Open a topic at the right question: the first one still unanswered IN THIS SESSION, or the
	 * first question when the session is finished (Review starts at the top).
	 */
	const openTopic = useCallback((topic) => {
		const q = findFirstQuestionInTopic(topic, x => !x.userAnswer) || findFirstQuestionInTopic(topic)
		if (q) navigate(`q/${q.code}`)
	}, [navigate])

	/*
	 * Every study action on a topic (101).
	 *
	 *   start   -> START or RESUME. Resuming is the same call; the server leaves started_at alone.
	 *   restart -> archive this attempt into history and begin a new one
	 *   review  -> walk back through a finished session. No session change at all: the answers
	 *              stay, the questions stay locked, and Change Answer is the only way to alter one.
	 *
	 * The session mutation is awaited BEFORE navigating, and the prepkit is refetched, so the
	 * question screen opens against the session the button just created rather than the one it
	 * replaced. Without the refresh a Restart would land on a screen still showing the archived
	 * attempt's answers.
	 *
	 * A failure leaves the student on the dashboard with the topic unchanged, which is the honest
	 * outcome — navigating anyway would show a session that was never created.
	 */
	const runTopicAction = useCallback(async (topic, kind) => {
		if (!prepKit || busyTopicId) return

		if (kind === 'review') {
			openTopic(topic)
			return
		}

		setBusyTopicId(topic.id)
		setSessionError(null)

		try {
			if (kind === 'restart') {
				await restartTopicSession(prepKit.id, topic.id)
			}
			else {
				await startTopicSession(prepKit.id, topic.id)
			}

			const updated = await refreshPrepKit()

			// navigate against the refreshed copy, so a Restart opens on question 1 and not on
			// whatever the archived session had left unanswered
			const fresh = (updated && findTopicInPrepKit(updated, topic.id)) || topic
			setDetailsTopic(null)
			openTopic(fresh)
		}
		catch (err) {
			console.error('topic session action failed', err)
			setSessionError(err.message || 'Something went wrong. Please try again.')
		}
		finally {
			setBusyTopicId(null)
		}
	}, [prepKit, busyTopicId, refreshPrepKit, openTopic])

	/*
	 * Restart asks first (181).
	 *
	 * Gated HERE rather than at each button, because this is the one place every restart passes
	 * through — the topic row's icon, its primary button when a topic is complete, a section's action,
	 * and the Daily Challenge's retake. Gating the buttons instead would have left whichever entry
	 * point was added next silently unguarded.
	 *
	 * Everything else runs straight through: start, resume and review are all recoverable by doing
	 * them again, and a prompt on each would be friction with nothing to protect.
	 */
	const handleTopicAction = useCallback((topic, kind) => {
		if (kind === 'restart') {
			setRestartTopic(topic)
			return
		}
		return runTopicAction(topic, kind)
	}, [runTopicAction])

	/*
	 * A section's one button: START -> RESUME -> REVIEW. It acts on the first topic in the section
	 * that still has work in it, or on the first topic once everything is done.
	 */
	const handleSectionAction = useCallback((section) => {
		const target = sectionActionTarget(section)
		if (!target) return

		handleTopicAction(target.topic, target.kind)
	}, [handleTopicAction])

	/*
	 * Continue Studying: back to the last question they were on (remembered in localStorage by
	 * QuestionView), otherwise the first topic with unfinished questions across the kit.
	 */
	const handleContinue = () => {
		const lastCode = getLastQuestion(prepKit.id)
		if (lastCode) {
			// the stored code may be stale (topic removed, different prepkit content)
			const {question} = findQuestionInPrepKit(prepKit, lastCode)
			if (question) return navigate(`q/${question.code}`)
		}

		/*
		 * Otherwise the first topic with an unfinished session. A topic whose session is complete
		 * is finished business, so it is skipped even though Review would still open it.
		 */
		for (const p of prepKit.products || []) {
			for (const domain of p.topics || []) {
				for (const child of domain.topics || []) {
					if (!isTopicSessionComplete(child)) return handleTopicAction(child, 'start')
				}
			}
		}
		// everything is complete → review the very first topic
		const first = prepKit.products?.[0]?.topics?.[0]?.topics?.[0]
		if (first) handleTopicAction(first, 'review')
	}

	if (!prepKit) return null

	const firstName = userProfile?.first_name

	// alignSelf:stretch + minWidth:0 override AppLayout's alignItems:center so the dashboard
	// fills the column at a stable width instead of shrink-wrapping to content (which made it
	// widen as topic pills expanded).
	return (
		<Box sx={{minHeight: '100%', width: '100%', alignSelf: 'stretch', minWidth: 0}}>
			<Helmet><title>{`PrepKit Dashboard - ${prepKit.name} - Birdsy`}</title></Helmet>
			{/* Fixed, breakpoint-aware width (definite widths, not max-width) so the layout
			    never grows to fit content — expanded topic rows wrap inside instead.
			    '100%' is a definite width here too: the parent is alignSelf:'stretch', so it
			    resolves against the column rather than shrink-wrapping to content.

			    Ladder (079): fill the window below lg — a 1199px laptop showing a 900px column
			    was the "narrow and crammed" complaint — then pin.

			    ONE rung, not two (101). The second rung widened the column to 1440 at xl, which
			    read as too wide once the topic grid replaced the inline pills: 1440 of cards is a
			    lot of horizontal travel for the eye. 1280 is the same value the lg rung already
			    used, so above 1200 the column simply stops growing. Below lg it still fills the
			    window, so the 079 complaint stays fixed — that was about narrow screens, and none
			    of them are affected by removing the top rung. */}
			<Box
				id="tour-prepkit-title"
				sx={{
					width: {xs: '100%', lg: 1280},
					maxWidth: '100%',
					mx: 'auto',
					px: {xs: 2, sm: 3, lg: 4},
					// pt is tighter than pb so the greeting starts high on the page (073)
					pt: isSmallScreen ? 1.5 : 2.5,
					pb: isSmallScreen ? 2 : 4,
				}}
			>

				{/*
				  * greeting header — Layer 1. Sized above the token scale on purpose (073):
				  * fontSize.display (20) is the top of the scale and is also what section
				  * titles use, so a page-level greeting has nowhere to go within it.
				  */}
				<Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: {xs: 3, md: 4}}}>
					<Box>
						<Typography sx={{fontSize: {xs: 24, md: 28}, fontWeight: custom.fontWeight.bold, color: custom.text.main, lineHeight: 1.25}}>
							{firstName ? `Welcome back, ${firstName} 👋` : 'Welcome back 👋'}
						</Typography>
						<Typography sx={{fontSize: custom.fontSize.md, color: custom.text.muted, mt: 0.5}}>
							{showWelcome ? 'Welcome to Birdsy!' : "Ready to build on your progress today? Let's dive in."}
						</Typography>
					</Box>
					{/*
					  * One slot, two occupants (170, 176). Tutor Style used to share this space and has
					  * moved to the navbar, where it is reachable from every screen rather than only
					  * from the one the student just left.
					  *
					  * A LIVE TRIAL WINS THE SLOT. While the clock is running, converting is the most
					  * useful thing this corner can offer; the ways into the material are all still on
					  * the page below — every topic row has its own Start/Resume/Review and the Daily
					  * Challenge names a specific topic.
					  *
					  * Once the trial is over — or on a kit that was never a trial — the slot goes back
					  * to Continue Studying. The countdown ticks, so the swap happens on its own at
					  * zero rather than on the next reload.
					  *
					  * DASHBOARD ONLY for the countdown: every other screen shows the fixed navbar chip
					  * instead, and ExpiringPrepKit holds that rule.
					  */}
					{!showWelcome && (showExpiryBanner
						? <TrialBanner variant="greeting" msLeft={trialMsLeft} isTrial={isTrialKit} onBuy={openBuy}/>
						: (
							<Button
								onClick={handleContinue}
								sx={{
									flexShrink: 0,
									textTransform: 'none',
									borderRadius: `${custom.radius.sm}px`,
									background: custom.indigo.primary,
									color: '#fff',
									px: 3,
									py: 1.25,
									fontSize: custom.fontSize.md,
									fontWeight: custom.fontWeight.semibold,
									'&:hover': {background: custom.indigo.hover},
								}}
							>
								{/* nothing answered yet, so there is nothing to continue (144) */}
								{progress === PROGRESS.NEW ? 'Start Studying →' : 'Continue Studying →'}
							</Button>
						)
					)}
				</Box>

				{/*
				  * HUD strip — three cards (072). Two of them warm up until the first topic is
				  * finished (167); `progress` is the same value the Daily Challenge panel reads, so the
				  * two cannot disagree about whether the student has completed one.
				  */}
				{!showWelcome && (
					<HudStrip
						custom={custom}
						readiness={readiness}
						onInsights={() => navigate('report')}
						achievements={achievements}
						onAchievements={() => setShowAchievements(true)}
						warmingUp={progress !== PROGRESS.GOING}
					/>
				)}

				{/*
				  * A session call failed (101). Shown rather than swallowed: the student pressed
				  * Start and nothing happened, and they need to know that rather than assume the
				  * button is broken.
				  */}
				{sessionError && (
					<Box sx={{
						display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
						background: `${custom.status.danger}14`,
						border: `1px solid ${custom.status.danger}59`,
						borderRadius: `${custom.radius.md}px`,
						px: 2, py: 1.25, mb: 2,
					}}>
						<Typography sx={{fontSize: custom.fontSize.sm, color: custom.text.main}}>
							{sessionError}
						</Typography>
						<IconButton size="small" onClick={() => setSessionError(null)}>
							<Close fontSize="small"/>
						</IconButton>
					</Box>
				)}

				{/* Daily Challenge (074) — renders nothing once every section is at 75%+ */}
			{/*
					 * The onboarding panel and the Daily Challenge share this slot (144): a student
					 * with nothing answered gets the panel, everyone else gets the challenge.
					 *
					 * The trial countdown no longer competes for it (170) — it moved up to the
					 * greeting row, so a student on a trial now sees the countdown AND their daily
					 * challenge rather than one displacing the other.
					 */}
				{!showWelcome && progress === PROGRESS.NEW && (
					<FirstQuestionPanel custom={custom} onStart={handleContinue}/>
				)}

				{!showWelcome && progress !== PROGRESS.NEW && (
					<DailyChallenge
						theme={theme}
						custom={custom}
						challenge={challenge}
						/*
						 * Restart ONLY for the improve challenge — that one is about re-attempting a
						 * topic already finished. Everything else resumes or begins a session, and
						 * restarting those would throw away answers that are still being credited.
						 *
						 * Tested on IMPROVE rather than on the finishing kinds (174): with two more
						 * kinds added, "anything that is not FINISH_FIRST restarts" would have quietly
						 * wiped a half-finished topic the moment the card offered to finish it.
						 */
						onAccept={(c) => handleTopicAction(c.topic, c.kind === 'IMPROVE' ? 'restart' : 'start')}
					/>
				)}

				{/*
				  * Course title — Layer 1, and the seam between the HUD cards above and the prepkit
				  * content below. Centred for exactly that reason: everything on either side of it is
				  * left-aligned, so a centred line reads as a divider rather than as another row.
				  *
				  * "Category — PrepKit", with the category carrying its own brand gradient. The
				  * category is taken from the first primary product, the same source the medallion
				  * uses (102) — fetchProduct reads vw_products, whose category json already carries
				  * the colours an admin picked on the Categories screen, so this costs no request.
				  */}
			{showWelcome ? (
					<Welcome
						prepKit={prepKit}
						user={user}
						userProfile={userProfile}
						onComplete={(up) => { setUserProfile(up); setWelcomeComplete(true) }}
					/>
				) : (
					/*
					 * No fill and no border (123). This wrapper drew a panel around every product on
					 * the page, and the products inside it stopped being cards at 118 for the same
					 * reason: bg.surface and bg.main are the same colour in light mode, so the box
					 * was invisible there and clearly drawn in dark. The course content is the page,
					 * not a component sitting on it.
					 *
					 * The Box stays — it groups the products, the empty-filter state, the
					 * supplemental section and the drawer under one parent — it simply paints
					 * nothing. The radius goes with the border it was rounding.
					 */
					<Box>
						{/*
						  * Topic filters, above the first product and centred (171).
						  *
						  * They used to be injected into the FIRST SURVIVING product's header, which
						  * had two costs. It tied a page-level control to whichever product happened
						  * to render first, and it meant the chips vanished with the products when a
						  * filter emptied the page — so the empty state below needed its own second
						  * copy of them just to give the reader a way back out. One row here, always
						  * mounted, removes both.
						  *
						  * Centred, so it reads as a control for the whole list rather than a heading
						  * for the product underneath it.
						  */}
						<Box sx={{mt: 6, mb: 3}}>
							<TopicFilters custom={custom} filter={topicFilter} counts={filterCounts} onFilter={setTopicFilter}/>
						</Box>

						{visiblePrimary.map((product) => (
							<ProductCard
								key={product.id}
								product={product}
								custom={custom}
								filter={topicFilter}
								busyTopicId={busyTopicId}
								onSectionAction={handleSectionAction}
								onTopicAction={handleTopicAction}
								onDetails={setDetailsTopic}
							/>
						))}

						{/*
						  * Every primary product filtered out. Just the message now (171) — the chips
						  * are above this and stay mounted, so the way back out is already on screen
						  * and does not have to be repeated here.
						  */}
						{visiblePrimary.length === 0 && (
							<Typography sx={{py: 2, textAlign: 'center', fontSize: custom.fontSize.md, color: custom.text.muted}}>
								No topics match this filter.
							</Typography>
						)}

						<SupplementalSection
							products={supplementalProducts}
							custom={custom}
							filter={topicFilter}
							busyTopicId={busyTopicId}
							onSectionAction={handleSectionAction}
							onTopicAction={handleTopicAction}
							onDetails={setDetailsTopic}
						/>

						<TopicDetailsDrawer
							topic={detailsTopic}
							prepKitId={prepKit.id}
							custom={custom}
							onClose={() => setDetailsTopic(null)}
							onAction={handleTopicAction}
							onQuestion={(q) => { setDetailsTopic(null); navigate(`q/${q.code}`) }}
						/>

						<Tour token="prepkit-dashboard" last="Continue"/>
					</Box>
				)}

				{/*
				  * Restart confirmation (181).
				  *
				  * Three ways out, not two. "Review instead" exists because it is the thing a student
				  * most often actually wanted: they finished the topic, they want to look at what they
				  * got wrong, and Restart was the button nearest that intention. Offering only
				  * Restart/Cancel would send them back to the row to hunt for the right control.
				  *
				  * The body says what survives, because "restart" does not say it. The attempt is
				  * archived rather than deleted — restartTopicSession stamps archived_at and opens a
				  * fresh session — so the old score stays readable in past performance, and a student
				  * hesitating over losing their 80% can be told they are not losing it.
				  */}
				<Dialog
					open={Boolean(restartTopic)}
					onClose={() => setRestartTopic(null)}
					maxWidth="xs"
					fullWidth
					PaperProps={{sx: {
						background: custom.bg.surface,
						borderRadius: `${custom.radius.lg}px`,
						backgroundImage: 'none',
					}}}
				>
					<DialogContent sx={{p: 3, pb: 1.5}}>
						<Typography sx={{
							fontSize: {xs: 18, md: 20},
							fontWeight: custom.fontWeight.bold,
							color: custom.text.main,
							lineHeight: 1.3,
						}}>
							Ready to restart “{restartTopic ? restartTopic.name : ''}”?
						</Typography>
						<Typography sx={{mt: 1, fontSize: custom.fontSize.sm, color: custom.text.muted, lineHeight: 1.55}}>
							You will start this topic again from the first question. Your score from this
							attempt is kept in past performance, so nothing is lost.
						</Typography>
					</DialogContent>

					<DialogActions sx={{px: 3, pb: 2.5, pt: 0, gap: 1, flexWrap: 'wrap'}}>
						{/* quietest first, so the destructive-looking one is not the easiest to hit */}
						<Button
							onClick={() => setRestartTopic(null)}
							sx={{textTransform: 'none', color: custom.text.muted, fontSize: custom.fontSize.sm}}
						>
							Cancel
						</Button>
						<Button
							onClick={() => { const t = restartTopic; setRestartTopic(null); runTopicAction(t, 'review') }}
							sx={{
								textTransform: 'none',
								fontSize: custom.fontSize.sm,
								fontWeight: custom.fontWeight.semibold,
								color: custom.text.main,
								border: `1px solid ${custom.border.strong}`,
								borderRadius: `${custom.radius.sm}px`,
								px: 2,
								'&:hover': {background: custom.bg.raisedHover},
							}}
						>
							No, let’s review
						</Button>
						<Button
							onClick={() => { const t = restartTopic; setRestartTopic(null); runTopicAction(t, 'restart') }}
							sx={{
								textTransform: 'none',
								fontSize: custom.fontSize.sm,
								fontWeight: custom.fontWeight.semibold,
								borderRadius: `${custom.radius.sm}px`,
								px: 2,
								background: custom.indigo.primary,
								color: '#fff',
								'&:hover': {background: custom.indigo.hover},
							}}
						>
							Yes, restart
						</Button>
					</DialogActions>
				</Dialog>

					<AchievementsPanel
					data={achievements}
					open={showAchievements}
					onClose={() => setShowAchievements(false)}
				/>

				<AchievementToast
					achievement={newBadge}
					remaining={moreBadges}
					onDismiss={dismissBadge}
				/>
			</Box>
		</Box>
	)
})

export default PrepKitDashboard
